import { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Search, Upload, Book, Shirt,
  ChevronDown, ChevronUp, Sparkles, X, Loader2, Copy, Check,
  FileText, ExternalLink, Eye, Maximize2, Image as ImageIcon, ChevronLeft, ChevronRight,
  User, Camera, CheckCheck, ShoppingCart, Plus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { STYLE_PAGE_MAPPING } from "@/data/stylePageMapping";
import { codeToFieldValue } from "@/data/codeReverseLookup";
import {
  addToStyleCart, getStyleCart, removeFromStyleCart, clearStyleCart, subscribeStyleCart,
} from "@/data/styleCart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PDF URLs for style guides with images
const STYLE_PDF_URLS = {
  jacket: "https://customer-assets.emergentagent.com/job_fittings-admin/artifacts/474g8w1e_MTM%20Men%20Jacket%20style--%20Lucy%20Sun.pdf",
  pants: "https://customer-assets.emergentagent.com/job_fittings-admin/artifacts/e2i8mhor_MTM%20Men%20Pant--Lucy%20Sun.pdf",
  vest: "https://customer-assets.emergentagent.com/job_fittings-admin/artifacts/xqt7ocq6_MTM%20Men%20Vest%20style%20--%20Lucy%20Sun.pdf",
  overcoat: "https://customer-assets.emergentagent.com/job_fittings-admin/artifacts/43ybrq1r_MTM%20Men%20coat%20style%20--%20Lucy%20Sun.pdf"
};

// Complete style code categories from the PDFs
const STYLE_CATEGORIES = {
  jacket: {
    label: "Jacket",
    icon: "🧥",
    pdfUrl: STYLE_PDF_URLS.jacket,
    categories: [
      { 
        name: "Canvas Construction", 
        codes: [
          "C-000A (Full Canvas)",
          "C-000B (Half Canvas)",
          "C-00C1 (Fused)",
          "C-00C2 (Medium Fused)",
          "C-OAAA (Full Handmade & Full Canvas)",
          "C-00AA (Half Handmade & Full Canvas)",
          "C-0AAB (Full Handmade & Half Canvas)",
          "C-0BAA (Half Handmade & Half Canvas)",
          "C-0AAD (Unconstructed Canvas)",
          "C-00C3 (Full Handmade, Unconstructed)",
          "C-00AD (Half Handmade, Unconstructed)"
        ] 
      },
      { 
        name: "Lapel Style", 
        codes: [
          "C-0001 (Notch Lapel)",
          "C-0002 (Peak Lapel)",
          "C-0003 (Semi Notch Lapel)",
          "C-0004 (Semi Peak Lapel)",
          "C-0005 (Shawl Lapel)",
          "C-0006 (Fish Lapel)",
          "C-0008 (Band Collar)",
          "C-001H (Round Peak Lapel)",
          "C-001J (Round Peak, Round Collar)",
          "C-001B (TQ Stand Collar w/ Button Tab)",
          "C-0000 (Zhongshan Lapel)",
          "C-001M (Notch Lapel Round Collar Point)",
          "C-001N (Peak Lapel Round Collar Point)",
          "C-001R (Diamond Collar)",
          "C-001Q (V Shape No Collar)",
          "C-001U (Duck Mouth Lapel)",
          "C-0681 (Sport Collar A - Notch w/ Square Tab)",
          "C-0682 (Sport Collar B - Notch w/ Pointed Tab)",
          "C-0683 (Sport Collar C - Notch w/ T Shape Tab)",
          "C-0684 (Sport Collar D - Notch w/ Loop)",
          "C-0685 (Sport Collar F - Notch w/ Round Tab)",
          "C-0686 (Sport Collar G - Removable Pointed Tab)",
          "C-0687 (Notch w/ Left Pointed Tab)",
          "C-0688 (Sport Collar J - Removable Round Tab)",
          "C-0689 (Peak Lapel Middle w/ Satin)",
          "C-0690 (Peak Lapel w/ Splice in Middle)",
          "C-0691 (Shawl Lapel End w/ Satin)",
          "C-0692 (Shawl on Notch Lapel)",
          "C-0693 (Notch w/ L&R Removable Tab)",
          "C-0694 (Notch w/ Contrast Satin Middle)",
          "C-0699 (Notch Lapel High Gorge)",
          "C-00F2 (Arc Underpart Shawl Lapel)",
          "C-00F3 (Knife Shape Shawl Lapel)",
          "C-00F5 (Shawl w/ Diagonal Satin)",
          "C-00F8 (Diamond Bottom Shawl)",
          "C-00F9 (Square Bottom Shawl)",
          "C-00FC (V Style Band Collar)",
          "C-00FD (V Shape Stand Collar Square Point)",
          "C-00FG (PK Lapel)",
          "C-00H4 (ER Shawl Collar)",
          "C-090A (A Shawl Lapel)",
          "C-090D (Big Shawl Lapel)",
          "C-090E (E Shawl Lapel)",
          "C-090M (Peak Lapel, Can Fold Up)",
          "C-090N (Notch Lapel, Can Fold Up)",
          "C-090P (Notch Lapel w/ P1 Collar)",
          "C-090R (Peak Lapel, Align at Collar Point)",
          "C-090Y (Swallow Tail Notch Lapel)",
          "C-090Z (Swallow Tail Peak Lapel)",
          "C-09A1 (Wider Collar Face Notch)",
          "C-090T (Notch w/ Flat Lapel Corner)",
          "C-09A5 (Notch Adding Shawl Style)",
          "C-09AH (Big Arc Peak Lapel)"
        ] 
      },
      { 
        name: "Lapel & Collar Corner", 
        codes: [
          "C-0531 (Standard Small Round Corner)",
          "C-0533 (Square Corner)",
          "C-0534 (Round Lapel Corner Square Collar)",
          "C-0535 (Point Lapel Corner)"
        ] 
      },
      { 
        name: "Lapel Width", 
        codes: [
          "C-00W0 (4.9cm/1.9in)", "C-00W1 (5.0cm/2.0in)", "C-00W2 (5.3cm/2.1in)", "C-00W3 (5.7cm/2.2in)",
          "C-00W4 (6.0cm/2.4in)", "C-00W5 (6.4cm/2.5in)", "C-00W6 (6.5cm/2.6in)", "C-00W7 (7.0cm/2.8in)",
          "C-00W8 (7.3cm/2.9in)", "C-00W9 (7.6cm/3.0in)", "C-00X0 (8.2cm/3.2in)", "C-00X1 (6.3cm/2.5in)",
          "C-00X2 (7.1cm/2.8in)", "C-00X3 (7.4cm/2.9in)", "C-00X4 (7.5cm/3.0in)", "C-00X5 (7.7cm/3.0in)",
          "C-00X6 (8.0cm/3.1in)", "C-00X7 (8.1cm/3.2in)", "C-00X8 (8.4cm/3.3in)", "C-00X9 (8.8cm/3.5in)",
          "C-00N0 (10.0cm/3.9in)", "C-00N1 (7.9cm/3.1in)", "C-00N2 (8.3cm/3.3in - Default)",
          "C-00N3 (8.6cm/3.4in)", "C-00N4 (8.9cm/3.5in)", "C-00N5 (9.0cm/3.5in)", "C-00N6 (9.5cm/3.7in)",
          "C-00N7 (9.8cm/3.9in)", "C-00N8 (10.2cm/4.0in)", "C-00N9 (10.3cm/4.1in)", "C-00NA (10.1cm/4.0in)",
          "C-00S0 (10.5cm/4.1in)", "C-00S1 (10.8cm/4.3in)", "C-00S2 (11.0cm/4.3in)", "C-00S3 (11.4cm/4.5in)",
          "C-00Y5 (13.0cm/5.1in)", "C-00Y7 (12.0cm/4.7in)", "C-00Y8 (14.0cm/5.5in)", "C-00Y9 (12.7cm/5.0in)"
        ] 
      },
      { 
        name: "Lapel Buttonhole Position", 
        codes: [
          "C-0541 (One Left)",
          "C-0542 (One Right)",
          "C-0543 (One Left & One Right)",
          "C-0544 (No Lapel Buttonhole)",
          "C-0545 (2 Left)",
          "C-0546 (2 Right)",
          "C-0549 (3 Left & 2 Right)",
          "C-0550 (3 Left)",
          "C-055G (4 Left)",
          "C-055L (2 Left & 2 Right)",
          "C-054Y (3 Left & 1 Right)",
          "C-055P (Shawl Lapel One Left)",
          "C-053M (Shawl One Left & One Right)",
          "C-054V (Shawl 2 Left & 1 Right)"
        ] 
      },
      { 
        name: "Lapel Buttonhole Style", 
        codes: [
          "C-055K (Real Straight 0.5cm)",
          "C-0551 (Normal Straight)",
          "C-0552 (Normal Round)",
          "C-0553 (Real Straight)",
          "C-0554 (Real Round)",
          "C-056Z (Real Straight 0.7cm)",
          "C-0555 (With Normal Flower Buttonhole)",
          "C-055C (Lapel Hand Stitching Fixed)"
        ] 
      },
      { 
        name: "Lapel Flower Style", 
        codes: [
          "C-0560 (No Flower Hole)",
          "C-0561 (Normal Flower Hole)",
          "C-0562 (Real Flower Hole)",
          "C-0563 (Flower Hole w/ Metal Ring)",
          "C-0564 (Double Flower Hole)"
        ] 
      },
      { 
        name: "Collar Tab Style", 
        codes: [
          "C-0570 (No Collar Tab)",
          "C-0571 (Standard Collar Tab)",
          "C-0572 (Button Collar Tab)",
          "C-0573 (Loop Collar Tab)",
          "C-0574 (Hidden Collar Tab)"
        ] 
      },
      { 
        name: "Collar Material", 
        codes: [
          "C-0580 (Same as Body)",
          "C-0581 (Contrasting Fabric)",
          "C-0582 (Velvet Collar)",
          "C-0583 (Satin Collar)",
          "C-0584 (Suede Collar)"
        ] 
      },
      { 
        name: "Front Buttons", 
        codes: [
          "C-0011 (Single Breast 1 Button)",
          "C-0012 (Single Breast 2 Buttons)",
          "C-0013 (Single Breast 3 Buttons)",
          "C-0014 (Single Breast 4 Buttons)",
          "C-0023 (Single Breast 5 Buttons)",
          "C-0025 (Single Breast 8 Buttons)",
          "C-0026 (Single Breast 6 Buttons)",
          "C-0027 (Single Breast 7 Buttons)",
          "C-001Z (SB 2 Buttons, Lapel Between 1st & 2nd)",
          "C-002B (SB 3 Buttons, Roll to Middle 1st & 2nd)",
          "C-002C (SB 3 Buttons, Roll to 2nd Button)",
          "C-002D (SB 4 Buttons, Roll to 2nd Button)",
          "C-002E (SB 4 Buttons, Roll to Middle 1st & 2nd)",
          "C-0015 (Double Breast 4 Buttons - 2 Fasten)",
          "C-0016 (Double Breast 6 Buttons - 2 Fasten)",
          "C-0017 (Double Breast 4 Buttons - 1 Fasten)",
          "C-0018 (Double Breast 6 Buttons - 1 Fasten)",
          "C-0019 (Double Breast 6 Buttons - 3 Fasten)",
          "C-0020 (Double Breast 8 Buttons - 4 Fasten)",
          "C-002F (Double Breast 2 Buttons - 1 Fasten)",
          "C-002K (DB 6 Buttons - 2 Fasten, Roll to Bottom)",
          "C-002N (Double Breast 8 Buttons - 3 Fasten)",
          "C-002S (Double Breast 5 Buttons - 4 Fasten)",
          "C-0A1A (SB 9 Buttons - 3 Group)",
          "C-0A1G (SB 6 Buttons - 2 Group)",
          "C-0A1J (SB 8 Buttons - 2 Group)"
        ] 
      },
      { 
        name: "Button Spacing", 
        codes: [
          "C-002R (Button Spacing 12.7cm)",
          "C-002L (Customer Appointed Spacing)"
        ] 
      },
      { 
        name: "Chest Dart", 
        codes: [
          "C-0521 (Normal Chest Dart)",
          "C-0522 (No Chest Dart)",
          "C-0523 (Jujube Pit Shape)",
          "C-0524 (Chest Dart Shoulder to Pocket)",
          "C-0620 (Chest Dart to Bottom)",
          "C-018B (No Chest Dart Princess Line)",
          "C-0187 (No Chest Dart Princess from Armhole)",
          "C-0188 (No Chest Dart Princess to Bottom)"
        ] 
      },
      { 
        name: "Chest Pocket", 
        codes: [
          "C-0100 (No Chest Pocket)",
          "C-0101 (Normal Chest Pocket)",
          "C-0102 (Arc Chest Pocket)",
          "C-0103 (Ship Shape Chest Pocket)",
          "C-0104 (Patch Ship Shape)",
          "C-0109 (Arc Shape 1cm Satin Piping)",
          "C-0110 (Double Besom)",
          "C-0111 (Double Besom w/ Loop & Button)",
          "C-0113 (Double Besom w/ Flap)",
          "C-0114 (Double Besom w/ Diamond Flap & Button)",
          "C-0115 (Double Besom w/ Zipper)",
          "C-0116 (Double Besom w/ Pointed Tab & Button)",
          "C-0117 (Double Besom w/ Square Tab & Button)",
          "C-0118 (Double Besom w/ Round Tab & Button)",
          "C-0119 (Double Besom w/ Flap & Button)",
          "C-0150 (Patch Pocket)",
          "C-0151 (Patch Pocket w/ Button)",
          "C-0121 (Patch w/ Single Pleat)",
          "C-0153 (Patch w/ Single Pleat & Flap)",
          "C-0154 (Patch w/ Flap)",
          "C-015A (Patch Small Round Flap)",
          "C-0155 (Patch w/ Single Pleat, Flap & Button)",
          "C-0156 (Patch w/ Flap & Button)",
          "C-0157 (Right Normal, No Left)",
          "C-0152 (Normal Right & Left)",
          "C-015D (Left & Right Patch)",
          "C-0124 (Patch w/ Double Pleats)",
          "C-0158 (Patch Box Pleat Pointed Flap Button)",
          "C-0159 (Patch Single & Box Pleat Button)",
          "C-0180 (Left Division w/ Button)",
          "C-0183 (L&R Division w/ Button)",
          "C-0186 (L&R Division)",
          "C-0188 (L&R W Shape)",
          "C-011A (1.2cm Width Welt)",
          "C-011B (2.0cm Width)",
          "C-011H (Double Besom Pointed Flap Buttonhole)",
          "C-011J (Double Besom 2nd Besom 4cm Below)",
          "C-011P (Double Besom Pointed Flap)",
          "C-00J2 (Trapezoid Chest Pocket)",
          "C-00J5 (Trapezoid 2.0cm Front, 2.5 Back)"
        ] 
      },
      { 
        name: "Chest Pocket Fixation", 
        codes: [
          "C-0190 (Sewn Fixed)",
          "C-0191 (Button Fixed)",
          "C-0192 (Snap Button Fixed)",
          "C-0193 (Velcro Fixed)"
        ] 
      },
      { 
        name: "Chest Pocket Material", 
        codes: [
          "C-01A0 (Same as Body)",
          "C-01A1 (Contrasting Fabric)",
          "C-01A2 (Satin Material)",
          "C-01A3 (Customer Appointed)"
        ] 
      },
      { 
        name: "Chest Pocket Welt Direction", 
        codes: [
          "C-01B0 (Horizontal Stripe)",
          "C-01B1 (Vertical Stripe)",
          "C-01B2 (Diagonal Stripe)",
          "C-01B3 (Customer Appointed)"
        ] 
      },
      { 
        name: "Lower Pocket Style", 
        codes: [
          "C-0200 (No Lower Pocket)",
          "C-0201 (Normal Flap Pocket)",
          "C-0202 (Double Besom)",
          "C-0203 (Straight Flap)",
          "C-0204 (Angle Flap)",
          "C-0205 (Round Flap)",
          "C-0206 (Double Besom w/ Loop & Button)",
          "C-0207 (Double Besom w/ Zipper)",
          "C-0210 (Slant Pocket)",
          "C-0211 (Slant Double Besom)",
          "C-0212 (Slant Flap)",
          "C-0220 (Hacking Pocket)",
          "C-0221 (Hacking Double Besom)",
          "C-0222 (Hacking Flap)",
          "C-0230 (Bellows Pocket)",
          "C-0240 (Napoleon Pocket)",
          "C-0250 (Welt Pocket)",
          "C-0260 (Patch Pocket)",
          "C-0261 (Patch w/ Flap)",
          "C-0262 (Patch w/ Single Pleat)",
          "C-0263 (Patch w/ Double Pleats)",
          "C-0264 (Patch w/ Box Pleat)",
          "C-0265 (Patch w/ Single Pleat & Flap)",
          "C-0266 (Patch w/ Box Pleat & Flap)",
          "C-0270 (Curved Patch)",
          "C-0280 (Safari Pocket)",
          "C-0208 (Double Besom w/ Tab & Button)",
          "C-0209 (Double Besom Pointed Tab Button)",
          "C-020A (Double Besom Square Tab Button)",
          "C-020B (Double Besom Round Tab Button)",
          "C-020C (Double Besom Diamond Flap Button)",
          "C-020D (Double Besom Pointed Flap Button)",
          "C-020E (Double Besom Angle Flap Button)",
          "C-020F (Double Besom Round Flap Button)"
        ] 
      },
      { 
        name: "Ticket Pocket", 
        codes: [
          "C-0300 (No Ticket Pocket)",
          "C-0301 (Right Ticket Pocket)",
          "C-0302 (Left Ticket Pocket)",
          "C-0303 (Right Ticket w/ Flap)",
          "C-0304 (Left Ticket w/ Flap)",
          "C-0310 (Ticket Double Besom)",
          "C-0311 (Ticket Double Besom w/ Flap)",
          "C-0312 (Ticket Double Besom w/ Button)",
          "C-0320 (Slant Ticket Pocket)",
          "C-0321 (Slant Ticket w/ Flap)"
        ] 
      },
      { 
        name: "Chest Pocket Posture", 
        codes: [
          "C-CP00 (Standard Position)",
          "C-CP01 (+0.5cm/0.2in Higher)",
          "C-CP02 (+1.0cm/0.4in Higher)",
          "C-CP03 (+1.5cm/0.6in Higher)",
          "C-CP04 (-0.5cm/0.2in Lower)",
          "C-CP05 (-1.0cm/0.4in Lower)",
          "C-CP06 (-1.5cm/0.6in Lower)"
        ] 
      },
      { 
        name: "Lower Pocket Flap Width", 
        codes: [
          "C-LF00 (Standard 5cm/2in)",
          "C-LF01 (4cm/1.6in)",
          "C-LF02 (4.5cm/1.8in)",
          "C-LF03 (5.5cm/2.2in)",
          "C-LF04 (6cm/2.4in)",
          "C-LF05 (6.5cm/2.6in)",
          "C-LF06 (7cm/2.8in)",
          "C-LF07 (7.5cm/3in)",
          "C-LF08 (8cm/3.1in)",
          "C-LF09 (8.5cm/3.3in)",
          "C-LF10 (9cm/3.5in)"
        ] 
      },
      { 
        name: "Ticket Pocket Posture", 
        codes: [
          "C-TP00 (Standard Position)",
          "C-TP01 (+0.5cm/0.2in Higher)",
          "C-TP02 (+1.0cm/0.4in Higher)",
          "C-TP03 (+1.5cm/0.6in Higher)",
          "C-TP04 (+2.0cm/0.8in Higher)",
          "C-TP05 (-0.5cm/0.2in Lower)",
          "C-TP06 (-1.0cm/0.4in Lower)",
          "C-TP07 (-1.5cm/0.6in Lower)",
          "C-TP08 (-2.0cm/0.8in Lower)"
        ] 
      },
      { 
        name: "Armhole Adjustment", 
        codes: [
          "C-AH00 (Standard Armhole)",
          "C-AH01 (+1cm/0.4in Higher)",
          "C-AH02 (+2cm/0.8in Higher)",
          "C-AH03 (-1cm/0.4in Lower)",
          "C-AH04 (-2cm/0.8in Lower)"
        ] 
      },
      { 
        name: "Shoulder Posture Adjustment", 
        codes: [
          "C-SP00 (Standard Posture)",
          "C-SP01 (Square +0.5cm/0.2in)",
          "C-SP02 (Square +1cm/0.4in)",
          "C-SP03 (Square +1.5cm/0.6in)",
          "C-SP04 (Square +2cm/0.8in)",
          "C-SP05 (Sloped -0.5cm/0.2in)",
          "C-SP06 (Sloped -1cm/0.4in)",
          "C-SP07 (Sloped -1.5cm/0.6in)",
          "C-SP08 (Sloped -2cm/0.8in)",
          "C-SP09 (Forward Posture)"
        ] 
      },
      { 
        name: "Outside Pocket Buttonhole Thread", 
        codes: [
          "C-0330 (Same as Body Thread)",
          "C-0331 (Contrasting Thread)",
          "C-0332 (Customer Appointed Thread)"
        ] 
      },
      { 
        name: "Pocket Besom Direction", 
        codes: [
          "C-0340 (Horizontal Stripe)",
          "C-0341 (Vertical Stripe)",
          "C-0342 (Diagonal Stripe)"
        ] 
      },
      { 
        name: "Pocket Flap Inner Side", 
        codes: [
          "C-0350 (Same as Body)",
          "C-0351 (Lining Material)",
          "C-0352 (Customer Appointed)"
        ] 
      },
      { 
        name: "Exchange Pocket Style", 
        codes: [
          "C-0360 (No Exchange Pocket)",
          "C-0361 (Standard Exchange)",
          "C-0362 (Double Besom Exchange)",
          "C-0363 (Patch Exchange)"
        ] 
      },
      { 
        name: "Vent Style", 
        codes: [
          "C-04N0 (No Vent)",
          "C-04N1 (Center Vent)",
          "C-04N2 (Side Vents)",
          "C-04N3 (Side Vents w/ Strap)",
          "C-04N4 (Hook Vent)",
          "C-04N5 (Inverted Pleat Vent)",
          "C-0420 (Center Vent w/ Button)",
          "C-0421 (Side Vents w/ Buttons)",
          "C-0430 (Short Center Vent)",
          "C-0431 (Short Side Vents)"
        ] 
      },
      { 
        name: "Vent Length Adjustment", 
        codes: [
          "C-0440 (Standard Length)",
          "C-0441 (+1cm/0.4in Length)",
          "C-0442 (+2cm/0.8in Length)",
          "C-0443 (+3cm/1.2in Length)",
          "C-0444 (+4cm/1.6in Length)",
          "C-0445 (+5cm/2.0in Length)",
          "C-0446 (+6cm/2.4in Length)",
          "C-0447 (-1cm/0.4in Length)",
          "C-0448 (-2cm/0.8in Length)",
          "C-0449 (-3cm/1.2in Length)",
          "C-044A (-4cm/1.6in Length)",
          "C-044B (-5cm/2.0in Length)",
          "C-044C (-6cm/2.4in Length)"
        ] 
      },
      { 
        name: "Lining Style", 
        codes: [
          "C-0501 (Full Lining)",
          "C-05C9 (Half Lining)",
          "C-05P7 (Quarter Lining)",
          "C-0403 (Unlined)",
          "C-0502 (Full Lining Piped)",
          "C-0503 (Full Lining w/ Action Back)",
          "C-0510 (Butterfly Lining)",
          "C-0520 (French Facing)",
          "C-0530 (Full Facing)"
        ] 
      },
      { 
        name: "Facing Style", 
        codes: [
          "C-0551 (Standard Facing)",
          "C-0552 (Extended Facing)",
          "C-0553 (Full Front Facing)",
          "C-0554 (Straight Facing)"
        ] 
      },
      { 
        name: "Lapel Material", 
        codes: [
          "C-0560 (Same as Body)",
          "C-0561 (Satin Lapel)",
          "C-0562 (Grosgrain Lapel)",
          "C-0563 (Customer Appointed)"
        ] 
      },
      { 
        name: "Inner Pic Stitch", 
        codes: [
          "C-0590 (No Inner Pic Stitch)",
          "C-0591 (Inner Pic Stitch 0.1cm/0.04in)",
          "C-0592 (Inner Pic Stitch 0.3cm/0.12in)",
          "C-0593 (Hand Inner Pic Stitch)"
        ] 
      },
      { 
        name: "Pic Stitch / Top Stitch", 
        codes: [
          "C-0571 (Lapel & Front Edge 0.1cm/0.04in)",
          "C-0572 (Lapel & Front Edge 0.6cm/0.24in)",
          "C-0573 (Full Lapel & Edge Pic Stitch)",
          "C-0574 (No Pic Stitch)",
          "C-0575 (Hand Pic Stitch)",
          "C-0576 (Machine Top Stitch)",
          "C-0577 (Double Needle Top Stitch)"
        ] 
      },
      { 
        name: "Shoulder Seam Pic Stitch", 
        codes: [
          "C-0610 (No Shoulder Pic Stitch)",
          "C-0611 (Shoulder Pic Stitch 0.1cm/0.04in)",
          "C-0612 (Shoulder Pic Stitch 0.3cm/0.12in)",
          "C-0613 (Shoulder Pic Stitch 0.6cm/0.24in)"
        ] 
      },
      { 
        name: "Armhole Pic Stitch", 
        codes: [
          "C-0620 (No Armhole Pic Stitch)",
          "C-0621 (Front Armhole Pic Stitch)",
          "C-0622 (Back Armhole Pic Stitch)",
          "C-0623 (Full Armhole Pic Stitch)"
        ] 
      },
      { 
        name: "Collar Front Pic Stitch", 
        codes: [
          "C-0630 (No Collar Pic Stitch)",
          "C-0631 (Collar Pic Stitch 0.1cm)",
          "C-0632 (Collar Pic Stitch 0.3cm)"
        ] 
      },
      { 
        name: "Back Center Pic Stitch", 
        codes: [
          "C-0640 (No Back Center Pic Stitch)",
          "C-0641 (Back Center Pic Stitch 0.1cm)",
          "C-0642 (Back Center Pic Stitch 0.3cm)"
        ] 
      },
      { 
        name: "Sleeve Seam Pic Stitch", 
        codes: [
          "C-0650 (No Sleeve Seam Pic Stitch)",
          "C-0651 (Sleeve Seam Pic Stitch 0.1cm)",
          "C-0652 (Sleeve Seam Pic Stitch 0.3cm)"
        ] 
      },
      { 
        name: "Chest Pocket Pic Stitch", 
        codes: [
          "C-0660 (No Chest Pocket Pic Stitch)",
          "C-0661 (Chest Pocket Pic Stitch 0.1cm)",
          "C-0662 (Chest Pocket Pic Stitch 0.3cm)"
        ] 
      },
      { 
        name: "Pocket Flap Pic Stitch", 
        codes: [
          "C-0670 (No Flap Pic Stitch)",
          "C-0671 (Flap Pic Stitch 0.1cm)",
          "C-0672 (Flap Pic Stitch 0.3cm)"
        ] 
      },
      { 
        name: "Vent Pic Stitch", 
        codes: [
          "C-0680 (No Vent Pic Stitch)",
          "C-0681 (Vent Pic Stitch 0.1cm)",
          "C-0682 (Vent Pic Stitch 0.3cm)"
        ] 
      },
      { 
        name: "Shoulder Style", 
        codes: [
          "C-0711 (Natural Shoulder)",
          "C-0712 (Soft Shoulder)",
          "C-0713 (Padded Shoulder)",
          "C-0714 (Roped Shoulder)",
          "C-0715 (Spalla Camicia)",
          "C-0716 (Con Rollino)",
          "C-0720 (Extended Shoulder)",
          "C-0721 (Narrow Shoulder)"
        ] 
      },
      { 
        name: "Shoulder Epaulet Style", 
        codes: [
          "C-0072 (No Epaulet)",
          "C-0073 (Button Epaulet)",
          "C-0074 (Pointed Epaulet)",
          "C-0075 (Round Epaulet)"
        ] 
      },
      { 
        name: "Canvas Presentation Sample", 
        codes: [
          "C-0076 (Send Canvas Sample)",
          "C-0077 (No Canvas Sample)"
        ] 
      },
      { 
        name: "Elbow Patch", 
        codes: [
          "C-0740 (No Elbow Patch)",
          "C-0741 (Standard Elbow Patch)",
          "C-0742 (Alcantara Elbow Patch)",
          "C-0743 (Leather Elbow Patch)",
          "C-0744 (Suede Elbow Patch)"
        ] 
      },
      { 
        name: "Shoulder Patch", 
        codes: [
          "C-0750 (No Shoulder Patch)",
          "C-0751 (Standard Shoulder Patch)",
          "C-0752 (Alcantara Shoulder Patch)",
          "C-0753 (Leather Shoulder Patch)"
        ] 
      },
      { 
        name: "Sleeve Slit Style", 
        codes: [
          "C-0761 (Open Slit)",
          "C-0762 (Closed Slit)",
          "C-0763 (No Slit)",
          "C-0764 (Slit w/ Buttons)"
        ] 
      },
      { 
        name: "Sleeve Cuff Style", 
        codes: [
          "C-0770 (Standard Cuff)",
          "C-0771 (French Cuff)",
          "C-0772 (Barrel Cuff)",
          "C-0773 (Turnback Cuff)"
        ] 
      },
      { 
        name: "Sleeve Buttonhole Style", 
        codes: [
          "C-0780 (Sham Buttonhole)",
          "C-0781 (Real Buttonhole)",
          "C-0782 (Functional Buttonhole)",
          "C-0783 (Last One Real)"
        ] 
      },
      { 
        name: "Sleeve Buttons", 
        codes: [
          "C-0811 (2 Buttons)",
          "C-0812 (3 Buttons)",
          "C-0813 (4 Buttons)",
          "C-0814 (5 Buttons)",
          "C-0815 (6 Buttons)",
          "C-0820 (Kissing Buttons)",
          "C-0821 (1cm Gap Buttons)",
          "C-0830 (Functional Buttonholes)",
          "C-0831 (Last Button Contrasting)",
          "C-0832 (All Buttons Contrasting)",
          "C-0840 (Mitered Buttons)",
          "C-0841 (Stacked Buttons)"
        ] 
      },
      { 
        name: "Button Style", 
        codes: [
          "C-0850 (Horn Buttons)",
          "C-0851 (Corozo Buttons)",
          "C-0852 (Mother of Pearl)",
          "C-0853 (Plastic Buttons)",
          "C-0854 (Metal Buttons)",
          "C-0855 (Leather Covered)",
          "C-0856 (Fabric Covered)",
          "C-0857 (Contrast Buttons)"
        ] 
      },
      { 
        name: "Tuxedo Options", 
        codes: [
          "C-0860 (Satin Lapel)",
          "C-0861 (Grosgrain Lapel)",
          "C-0862 (Satin Pocket Welt)",
          "C-0863 (Satin Covered Buttons)",
          "C-0864 (Satin Collar)"
        ] 
      },
      { 
        name: "Lining Design", 
        codes: [
          "C-0870 (Solid Color Lining)",
          "C-0871 (Striped Lining)",
          "C-0872 (Paisley Lining)",
          "C-0873 (Custom Print Lining)",
          "C-0874 (Customer Appointed)"
        ] 
      },
      { 
        name: "Sweat Pad Style", 
        codes: [
          "C-0880 (No Sweat Pad)",
          "C-0881 (Standard Sweat Pad)",
          "C-0882 (Extended Sweat Pad)",
          "C-0883 (Removable Sweat Pad)"
        ] 
      },
      { 
        name: "Sweat Pad Requirement", 
        codes: [
          "C-0884 (Small Size)",
          "C-0885 (Medium Size)",
          "C-0886 (Large Size)",
          "C-0887 (Customer Appointed)"
        ] 
      },
      { 
        name: "Inner Pocket Style", 
        codes: [
          "C-0900 (No Inner Pocket)",
          "C-0901 (Standard Inner Pocket)",
          "C-0902 (Double Besom Inner)",
          "C-0903 (Inner w/ Zipper)",
          "C-0904 (Inner w/ Button)",
          "C-0905 (Inner w/ Flap)"
        ] 
      },
      { 
        name: "Left Inner Pocket", 
        codes: [
          "C-0910 (No Left Inner)",
          "C-0911 (Standard Left Inner)",
          "C-0912 (Left Inner w/ Zipper)",
          "C-0913 (Left Inner w/ Button)",
          "C-0914 (Left Inner w/ Flap)"
        ] 
      },
      { 
        name: "Right Inner Pocket", 
        codes: [
          "C-0920 (No Right Inner)",
          "C-0921 (Standard Right Inner)",
          "C-0922 (Right Inner w/ Zipper)",
          "C-0923 (Right Inner w/ Button)",
          "C-0924 (Right Inner w/ Flap)"
        ] 
      },
      { 
        name: "Inner Pocket Besom Design", 
        codes: [
          "C-0930 (Same as Body)",
          "C-0931 (Contrasting Fabric)",
          "C-0932 (Customer Appointed)"
        ] 
      },
      { 
        name: "Upper Forward Pocket", 
        codes: [
          "C-0940 (No Upper Forward Pocket)",
          "C-0941 (Standard Upper Forward)",
          "C-0942 (Upper Forward w/ Button)"
        ] 
      },
      { 
        name: "Left Pen/Cell Phone Pocket", 
        codes: [
          "C-0950 (No Left Pen Pocket)",
          "C-0951 (Standard Left Pen Pocket)",
          "C-0952 (Left Cell Phone Pocket)",
          "C-0953 (Left Pen + Cell Pocket)"
        ] 
      },
      { 
        name: "Right Pen/Cell Phone Pocket", 
        codes: [
          "C-0960 (No Right Pen Pocket)",
          "C-0961 (Standard Right Pen Pocket)",
          "C-0962 (Right Cell Phone Pocket)",
          "C-0963 (Right Pen + Cell Pocket)"
        ] 
      },
      { 
        name: "Name Card Pocket", 
        codes: [
          "C-0970 (No Name Card Pocket)",
          "C-0971 (Left Name Card Pocket)",
          "C-0972 (Right Name Card Pocket)",
          "C-0973 (Both Name Card Pockets)"
        ] 
      },
      { 
        name: "Right Lower Name Card Pocket", 
        codes: [
          "C-0980 (No Right Lower Pocket)",
          "C-0981 (Standard Right Lower)",
          "C-0982 (Right Lower w/ Button)"
        ] 
      },
      { 
        name: "MP3 Pocket", 
        codes: [
          "C-0990 (No MP3 Pocket)",
          "C-0991 (Standard MP3 Pocket)",
          "C-0992 (MP3 w/ Headphone Hole)"
        ] 
      },
      { 
        name: "Inner Pocket Seal Design", 
        codes: [
          "C-09A0 (No Seal)",
          "C-09A1 (Standard Seal)",
          "C-09A2 (Bartack Seal)",
          "C-09A3 (Customer Appointed)"
        ] 
      },
      { 
        name: "Brand Label Position", 
        codes: [
          "C-0103 (Standard Position)",
          "C-0104 (Left Inside Panel)",
          "C-0105 (Right Inside Panel)",
          "C-0106 (Customer Appointed)"
        ] 
      },
      { 
        name: "Customer Name Monogram", 
        codes: [
          "C-0110 (No Monogram)",
          "C-0111 (Left Inside Panel)",
          "C-0112 (Right Inside Panel)",
          "C-0113 (Under Pocket)",
          "C-0114 (Customer Appointed)"
        ] 
      },
      { 
        name: "Collar Loop Style", 
        codes: [
          "C-0120 (No Collar Loop)",
          "C-0121 (Standard Loop)",
          "C-0122 (Chain Loop)",
          "C-0123 (Hidden Loop)"
        ] 
      },
      { 
        name: "Sleeve Label Position", 
        codes: [
          "C-0130 (No Sleeve Label)",
          "C-0131 (Inside Left Sleeve)",
          "C-0132 (Inside Right Sleeve)",
          "C-0133 (Both Sleeves)"
        ] 
      },
      { 
        name: "Customer Indicated Sticker", 
        codes: [
          "C-0140 (No Sticker)",
          "C-0141 (Standard Sticker)",
          "C-0142 (Customer Appointed)"
        ] 
      },
      { 
        name: "Fabric Information Tag", 
        codes: [
          "C-0150 (No Fabric Tag)",
          "C-0151 (Standard Fabric Tag)",
          "C-0152 (Detailed Fabric Tag)"
        ] 
      },
      { 
        name: "Stomach Size Adjustment", 
        codes: [
          "C-0160 (No Adjustment)",
          "C-0161 (+2cm Adjustment)",
          "C-0162 (+4cm Adjustment)",
          "C-0163 (-2cm Adjustment)",
          "C-0164 (Customer Appointed)"
        ] 
      },
      // === POSTURE CODES ===
      { 
        name: "Chest Pocket Posture", 
        codes: [
          "C-06R2 (Chest Pocket Width 3.5cm)",
          "C-06R3 (Chest Pocket Width 3.2cm)",
          "C-06QF (Chest Pocket Length 8.5cm)",
          "918 (Chest Pocket Lower 2cm)",
          "9PW (Chest Pocket Lower 3cm)",
          "9PY (Chest Pocket Lower 4cm)",
          "9VM (Chest Pocket Lower 5cm)"
        ] 
      },
      { 
        name: "Lower Pocket Flap Width", 
        codes: [
          "03AC (Bottom Pocket Flap 3.5cm Width)",
          "03A3 (Lower Pocket Flap Width 4.0cm)",
          "03A4 (Lower Pocket Flap Width 4.3cm)",
          "03A5 (Lower Pocket Flap Width 4.5cm)",
          "03A6 (Lower Pocket Flap Width 4.8cm)",
          "03A7 (Lower Pocket Flap Width 5.0cm)",
          "03A8 (Lower Pocket Flap Width 5.3cm)",
          "03A9 (Lower Pocket Flap Width 5.5cm)",
          "03C1 (Lower Pocket Flap Width 6.0cm)",
          "03C2 (Lower Pocket Flap Width 6.5cm)",
          "03C3 (Lower Pocket Flap Width 7.0cm)"
        ] 
      },
      { 
        name: "Ticket Pocket Posture", 
        codes: [
          "03B0 (Ticket 5.7cm Width, 7.0cm to Lower Pocket)",
          "03B4 (Ticket 3.5cm Width, 6.0cm to Lower Pocket)",
          "03B5 (Ticket Flap 3.8cm, 6.5cm to Lower Pocket)",
          "03B6 (Ticket Flap 4.0cm, 6.5cm to Lower Pocket)",
          "03B7 (Ticket Flap 4.3cm, 7.0cm to Lower Pocket)",
          "03B8 (Ticket 4.5cm Width, 7.0cm to Lower Pocket)",
          "03B9 (Ticket 4.8cm Width, 7.0cm to Lower Pocket)",
          "03BC (Ticket 5.5cm Width, 8cm to Main Pocket)",
          "03BG (Ticket 6.5cm Width, 5cm to Main Pocket)"
        ] 
      },
      { 
        name: "Back Vent Length Adjustment", 
        codes: [
          "C-04K1 (Shorten Back Vent by 2cm)",
          "C-04K2 (Shorten Back Vent by 4cm)",
          "C-04K3 (Shorten Back Vent by 6cm)",
          "C-04K7 (Shorten Back Vent by 8cm)",
          "C-04K8 (Shorten Back Vent by 10cm)",
          "C-04K0 (Shorten Back Vent by 12cm)",
          "C-04K9 (Shorten Back Vent by 14cm)",
          "C-04L1 (Shorten Back Vent by 16cm)",
          "C-04L2 (Shorten Back Vent by 18cm)",
          "C-04K4 (Lengthen Back Vent by 2cm)",
          "C-04K5 (Lengthen Back Vent by 4cm)",
          "C-04K6 (Lengthen Back Vent by 6cm)",
          "C-04KA (Lengthen Back Vent by 3cm)"
        ] 
      },
      { 
        name: "Armhole Adjustment", 
        codes: [
          "09P0 (Armhole Higher 1.0cm)",
          "09P1 (Armhole Higher 0.5cm)",
          "09P2 (Armhole Lower 0.5cm)",
          "09P3 (Armhole Lower 1.0cm)",
          "09P4 (Armhole Lower 1.5cm)"
        ] 
      },
      { 
        name: "Shoulder Posture Adjustment", 
        codes: [
          "09AC (Handle 0.4cm More - Slope Shoulder)",
          "09AD (Handle 0.8cm More - Slope Shoulder)",
          "09AE (Handle 0.4cm More - Shrug Shoulder)",
          "09AG (Handle 0.8cm More - Shrug Shoulder)",
          "09B9 (Right Back Slope Shoulder 1.0cm)",
          "09BC (Left Shoulder Back Slope 1.0cm)",
          "09BD (Right Shoulder Back Slip 0.5cm)",
          "09BJ (Right Shoulder Back Sliding 2.0cm - Separate Cut)",
          "09BK (Left Shoulder Back Sliding 2.0cm - Separate Cut)",
          "09BM (Left Shoulder Back Sliding 1.5cm - Separate Cut)"
        ] 
      }
    ]
  },
  pants: {
    label: "Pants",
    icon: "👖",
    pdfUrl: STYLE_PDF_URLS.pants,
    categories: [
      { 
        name: "Waistband Height", 
        codes: [
          "T-301C (3.5cm Width)",
          "T-301E (3.8cm Width)",
          "T-301D (4.0cm Width)",
          "T-3015 (4.5cm Width)",
          "T-301F (5.0cm Width)"
        ] 
      },
      { 
        name: "Waistband Style", 
        codes: [
          "T-3010 (Standard Waistband)",
          "T-3011 (Front & Back Continuous)",
          "T-3012 (Front Uncontinued, Back Continued)",
          "T-3013 (Front Continued)",
          "T-3001 (Standard Pant Style)"
        ] 
      },
      { 
        name: "Pleats Style", 
        codes: [
          "T-3020 (Plain Front)",
          "T-3021 (Single Pleat Forward Sideseam)",
          "T-3022 (Single Pleat Forward Fly)",
          "T-3023 (Double Pleats Forward Fly)",
          "T-3024 (Triplicate Pleats Forward Sideseam)",
          "T-3025 (Double Pleats Forward Sideseam)",
          "T-3026 (Triplicate Pleats Forward Fly)",
          "T-3027 (One Opposite Pleat)",
          "T-3028 (Opposite Pleat + Pleat Forward Sideseam)",
          "T-3029 (Opposite Pleat + Pleat Forward Fly)",
          "T-302A (Pleat Forward Sideseam + Opposite)",
          "T-302B (Pleat Forward Fly + Opposite)",
          "T-302J (Box Pleat + Pleat Forward Sideseam)",
          "T-302K (Front Pleats in Line w/ Belt Loops)",
          "T-302M (Plain Front w/ Single Dart)",
          "T-302P (One Box Pleat)",
          "T-302R (Single Pleat w/ Side Dart)",
          "T-302Y (Plain Front w/ Double Darts)"
        ] 
      },
      { 
        name: "Pleat Depth", 
        codes: [
          "T-3027 (1.5cm Pleat Depth)",
          "T-302H (2.0cm Pleat Depth)",
          "T-302E (2.5cm Pleat Depth)",
          "T-302F (3.2cm Pleat Depth)",
          "T-302G (3.8cm Pleat Depth)",
          "T-302J (4.4cm Pleat Depth)"
        ] 
      },
      { 
        name: "Front Pleat Net Length", 
        codes: [
          "T-3020 (Opened Pleats - 0cm)",
          "T-3021 (1.3cm Length)",
          "T-3022 (1.9cm Length)",
          "T-30Z3 (2.5cm Length)",
          "T-3024 (3.2cm Length)",
          "T-3025 (3.8cm Length)"
        ] 
      },
      { 
        name: "Front Pocket Style", 
        codes: [
          "T-3100 (2.0cm Slant Pocket)",
          "T-3101 (3.2cm Slant Pocket)",
          "T-3102 (5.1cm Slant Pocket)",
          "T-3103 (Very Slant Pocket)",
          "T-3104 (2.5 Slant Pocket)",
          "T-3105 (5.1 Slant + Besom Ticket 8cm)",
          "T-3106 (5.1 Slant + Decorative Besom Ticket)",
          "T-3107 (5.1 Slant + Welt Ticket)",
          "T-3120 (2.0cm Slant w/ Stripe)",
          "T-3121 (3.2cm Slant w/ Stripe)",
          "T-3122 (5.1cm Slant w/ Stripe)",
          "T-3123 (Very Slant w/ Stripe)",
          "T-3124 (2.5 Slant w/ Stripe)",
          "T-3130 (Sideseam Pocket)",
          "T-3131 (Double Besom Pocket)",
          "T-3132 (Welt Pocket)",
          "T-3133 (Square Jeans Pocket)",
          "T-3134 (Diamond Jeans Pocket)",
          "T-3135 (Arc Jean Pocket)",
          "T-3136 (Round Jeans Pocket)",
          "T-3138 (DL Jeans Pocket)",
          "T-3139 (Straight Double Besom)",
          "T-313A (Straight Besom w/ Square Flap)",
          "T-313B (Slant Pocket w/ Square Point)",
          "T-313C (No Front Pocket)",
          "T-313D (Besom w/ Zipper)",
          "T-313E (Pocket w/ Zipper)"
        ] 
      },
      { 
        name: "Front Pocket Opening", 
        codes: [
          "T-3140 (Split Seam Customer Fabric)",
          "T-3142 (Slant Customer Fabric)",
          "T-314K (Opening -1.0cm)",
          "T-314L (Opening -2.0cm)",
          "T-314M (Opening +2.0cm)",
          "T-314N (Opening +1.0cm)",
          "T-314P (Front Pocket Welt w/ Satin)",
          "T-3153 (Jean Pocket 0.3cm Piping)",
          "T-3154 (Slant 0.3cm Fabric Piping)"
        ] 
      },
      { 
        name: "Front Pocketing", 
        codes: [
          "T-3144 (Add 2cm Depth)",
          "T-3145 (Reduce 2cm Depth)",
          "T-3146 (Reduce 3cm Depth)",
          "T-3147 (Reduce 4cm Depth)",
          "T-3149 (0.8cm Splice Customer Fabric)",
          "T-3150 (1.0cm Splice Customer Fabric)",
          "T-3151 (Add 5.0cm Depth)",
          "T-3163 (Reduce 6cm Depth)",
          "T-314E (Add 4cm Width & Depth)"
        ] 
      },
      { 
        name: "Waistband Pocket", 
        codes: [
          "T-3161 (Left Waist Card Bag)",
          "T-3162 (Right Waist Card Bag)",
          "T-3164 (Left & Right Car Pockets 12cm)",
          "T-3167 (Left Car Pocket 12cm)",
          "T-3168 (Right Car Pocket 12cm)"
        ] 
      },
      { 
        name: "Watch Pocket Style", 
        codes: [
          "T-3169 (No Watch Pocket)",
          "T-3170 (Normal Left Watch Pocket)",
          "T-3171 (Besom Left Pocket)",
          "T-3174 (Left w/ Diamond Flap)",
          "T-3175 (Right w/ Square Flap)",
          "T-3177 (Left w/ Square Flap)",
          "T-3178 (Double Besom Left)",
          "T-3179 (Normal Left Watch)",
          "T-3180 (Normal 7.6x7.6cm)",
          "T-317A (Left Decorative Diamond Flap Button)",
          "T-318B (Besom Left + Right Diamond Flap)",
          "T-318R (Right Diamond Flap + Left Watch)",
          "T-318T (Double Besom + Left Watch)",
          "T-318Z (Normal Left + Right Diamond Flap)"
        ] 
      },
      { 
        name: "Back Pocket Style", 
        codes: [
          "T-31A0 (No Back Pocket)",
          "T-3200 (Double Besom - No Buttons)",
          "T-3201 (Double Besom w/ Button)",
          "T-3203 (Double Besom Pointed Tab & Button)",
          "T-3205 (Back Pocket w/ Zipper)",
          "T-3206 (Double Besom Tab & Button)",
          "T-3210 (Back Pocket w/ Flap)",
          "T-320L (Double Besom Hide Button)",
          "T-320N (Double Welt Left Decorative)",
          "T-320P (Welt Back w/ Zipper)",
          "T-320V (Double Besom Decorative w/ Buttons)",
          "T-3220 (Double Besom Decorative)",
          "T-3221 (Besom Back Pocket)",
          "T-3222 (Besom w/ Button)",
          "T-3224 (Welt w/ Loop & Button)",
          "T-31A7 (Double Besom 1.0cm Upper, 0.5cm Lower)",
          "T-32C1 (Double Besom Square Tab Button)",
          "T-32C2 (Double Besom Round Tab Button)",
          "T-32LA (Double Welt Pointed Tab Inside Button)",
          "T-32PA (Double Welt Pointed Tab Button Inside)"
        ] 
      },
      { 
        name: "Back Pocket Position", 
        codes: [
          "T-32B1 (Both Sides Double Besom)",
          "T-32B2 (Left Only Double Besom)",
          "T-32B3 (Right Only Double Besom)",
          "T-32B4 (Both Sides w/ Flap)",
          "T-32B5 (Left Only w/ Flap)",
          "T-32B6 (Right Only w/ Flap)"
        ] 
      },
      { 
        name: "Bottom Style", 
        codes: [
          "T-3600 (Plain Hem)",
          "T-3602 (3.2cm Cuff)",
          "T-3603 (3.8cm Cuff)",
          "T-3604 (4.4cm Cuff)",
          "T-3605 (5.0cm Cuff)",
          "T-3606 (5.7cm Cuff)",
          "T-3607 (6.3cm Cuff)",
          "T-3608 (2.5cm Cuff)",
          "T-3609 (2.0cm Cuff)",
          "T-360A (No Finish)",
          "T-360B (Extra Long for Adjustment)",
          "T-360C (Cuff w/ Button)",
          "T-360D (Inside Cuff)"
        ] 
      },
      { 
        name: "Belt Loop Style", 
        codes: [
          "T-3329 (No Belt Loop)",
          "T-3330 (Regular Belt Loops)",
          "T-3331 (Double Belt Loop)",
          "T-3332 (Belt Loop Width 2.0cm)",
          "T-3333 (Big Head Belt Loop)",
          "T-3334 (Buttons on 2.0cm Slant Loop)",
          "T-3335 (Buttons on Belt Loop)",
          "T-3336 (Double Belt Loop Front Only)",
          "T-3337 (Belt Loop Width 1.3cm)",
          "T-3338 (Belt Loop Width 3.8cm)",
          "T-3339 (Double Slant Belt Loop)",
          "T-33D1 (Belt Loop Width 3.8cm)",
          "T-333A (0.1cm Double Top Stitch on Loop)",
          "T-333B (No Top Stitch on Loop)",
          "T-333C (0.1cm Top Stitch Customer Thread)",
          "T-333D (First Loop 3.0cm Wide)",
          "T-333E (X-Style Belt Loops Front)",
          "T-333F (3.2cm Wide Belt Loop)",
          "T-333H (Left Front Double Loop Only)",
          "T-333J (Front Loop Fronted 1cm)",
          "T-333K (Double Loops Both Sides Back Crotch)",
          "T-333M (X-Shaped 6 Groups)",
          "T-33B1 (Belt Loop Not Sewn - Delivery)",
          "T-33BA (Slant Belt Loop)",
          "T-33B7 (V Shape on Back Middle Loop)"
        ] 
      },
      { 
        name: "Belt Loop Quantity", 
        codes: [
          "T-3714 (5 Loops)",
          "T-3715 (6 Loops)",
          "T-3716 (7 Loops)",
          "T-3717 (8 Loops)",
          "T-3718 (9 Loops)",
          "T-361B (3 Loops - 2 Front, 1 Back)",
          "T-361C (Right Waistband 1 Loop 2cm from Fly)",
          "T-361N (Only 1 Loop Right Front)"
        ] 
      },
      { 
        name: "Belt Loop Position", 
        codes: [
          "T-3345 (Bottom Loop in Waist)",
          "T-3347 (Normal Dropped Loops)",
          "T-3344 (Inside the Waist)",
          "T-334C (1cm Below Waist Seam)",
          "T-334E (2cm Below Waistband Seam)",
          "T-334F (2cm Above Waist Seam)"
        ] 
      },
      { 
        name: "Waist Adjustment", 
        codes: [
          "T-3349 (Standard Waistband)",
          "T-3350 (Movable & Adjust Waist)",
          "T-3351 (Adjust Buttons)",
          "T-3352 (Adjust Buckle)",
          "T-3356 (Side Adjust Tab)",
          "T-3357 (Adjust Elastic)",
          "T-3358 (Special Buckle for IMC)",
          "T-335A (Adjust Buttons - 3 Buttons)",
          "T-33T1 (Dark Color Buckle w/ Loop)",
          "T-33T2 (Silver Adjust Buckle w/ Loop)",
          "T-33T4 (Pointed Adjust Buttons)",
          "T-33T5 (Round Adjust Buttons)",
          "T-33T7 (Side Tabs on Waistband)",
          "T-33T8 (Silver Buckle Side Tabs)",
          "T-33AK (Square Adjust Buckle w/ Loop)",
          "T-335H (Pointed Inner Waist Button)",
          "T-335J (Specify Buckle on Side Seams)"
        ] 
      },
      { 
        name: "Back Center Seam", 
        codes: [
          "T-334D (2.5cm Belt Loop on Back Center)",
          "T-3346 (Loop in Center Back Waistband)",
          "T-335B (I Shape Back Slotting Flat Bartack)",
          "T-3353 (I Shape Back Waist Slotting)",
          "T-3355 (Back Central V Shape Opening)",
          "T-335C (Back Central V Shape Vent)"
        ] 
      },
      { 
        name: "Fly Style", 
        codes: [
          "T-3400 (Fish-Mouth Fly)",
          "T-3401 (Straight Fly)",
          "T-3402 (Fish Claw Fly)",
          "T-3403 (French Fly)",
          "T-3404 (Small Fish Claw Square Under Fly)",
          "T-3405 (Small Fish Claw Round Under Fly)",
          "T-3406 (Pointed Fly)",
          "T-3408 (Texas Fly)",
          "T-340E (Point Trouser Inner Fly Extension)",
          "T-340G (Texas Fly 1.7cm Needle Eyes)"
        ] 
      },
      { 
        name: "Heel Guard", 
        codes: [
          "T-33BB (No Heel Guard)",
          "T-3611 (Heel Guard)",
          "T-3613 (Heel Guard in Right Back Pocket)",
          "T-3614 (Full Circle Heel Guard at Bottom)",
          "T-3616 (Heel Guard Shipped with Pant)",
          "T-361G (Customer Appointed Heel Guard)",
          "T-361H (Two Pairs - One Bottom, One Shipped)",
          "T-361L (Full Circle Finished Ribbon)",
          "T-361M (Fabric Label Edge w/ Heel Guard)",
          "T-361P (Full Circle Shipped with Pant)",
          "T-33B8 (Heel Guard Width 3cm)"
        ] 
      },
      { 
        name: "Ironware Logo Tag", 
        codes: [
          "T-33B9 (Tag on First Left Belt Loop)",
          "T-33BC (Tag on First Right Belt Loop)"
        ] 
      },
      { 
        name: "Beltloop Bartack Style", 
        codes: [
          "T-3340 (Upper Horizontal Bartack)",
          "T-3341 (Upper No Bartack)",
          "T-3342 (Straight Bartack Front & Bottom)",
          "T-3343 (Upper Horizontal Bartack Both Sides)"
        ] 
      }
    ]
  },
  vest: {
    label: "Vest",
    icon: "🦺",
    pdfUrl: STYLE_PDF_URLS.vest,
    categories: [
      { 
        name: "Collar Style", 
        codes: [
          "V-4000 (Standard V Shape - No Collar)",
          "V-4001 (U Shape - No Collar)",
          "V-4002 (Shawl Lapel w/ Collar Band)",
          "V-4003 (Shawl Lapel - No Collar Band)",
          "V-4004 (Notch Lapel - No Collar Band)",
          "V-4005 (Notch Lapel w/ Collar Band)",
          "V-4006 (Peak Lapel - No Collar Band)",
          "V-4007 (Peak Lapel w/ Collar Band)",
          "V-4011 (Semi-Notch Lapel - No Collar Band)",
          "V-4012 (Semi-Notch Lapel w/ Collar Band)",
          "V-4013 (Semi-Peak Lapel - No Collar Band)",
          "V-4014 (Semi-Peak Lapel w/ Collar Band)",
          "V-4015 (Rounded Notch - No Collar Band)",
          "V-4016 (Rounded Notch w/ Stand Collar)",
          "V-4017 (Notch Round Collar Point - No Band)",
          "V-4018 (Rounded Notch Rounded Collar)",
          "V-4019 (Notch Round Collar Point - No Band)",
          "V-4020 (Rounded Notch Rounded Collar)",
          "V-400U (U Shape Shawl - No Collar Band)",
          "V-400V (Shawl w/ Collar Band)",
          "V-400W (Shawl w/ Collar Band 3.8cm, 0.15 Pic)",
          "V-400Y (Peak w/ Collar Band 5.5cm)",
          "V-401R (Narrow Peak w/ Collar Band)"
        ] 
      },
      { 
        name: "Lapel Adjustment", 
        codes: [
          "V-40E1 (Lapel Length +1cm)",
          "V-40E2 (Lapel Length +2cm)",
          "V-40E3 (Lapel Length -1cm)",
          "V-40E4 (Lapel Length -2cm)"
        ] 
      },
      { 
        name: "Lapel Buttonhole Position", 
        codes: [
          "V-4541 (Left Lapel Sham Buttonhole)",
          "V-4542 (Right Lapel Sham Buttonhole)",
          "V-4543 (Right & Left Sham Buttonhole)",
          "V-4544 (No Lapel Sham Buttonhole)",
          "V-4545 (Left Two Sham Buttonholes)",
          "V-4546 (Right Two Sham Buttonholes)",
          "V-4549 (R & L Two Sham Buttonholes)",
          "V-4550 (Left Four Sham Buttonholes)",
          "V-455G (Left Three Sham Buttonholes)",
          "V-455L (Left Three, Right Two Sham)"
        ] 
      },
      { 
        name: "Buttonhole Style", 
        codes: [
          "V-455K (Real Straight 0.5cm Lapel)",
          "V-4551 (Normal Straight Lapel)",
          "V-4552 (Normal Round Point Lapel)",
          "V-4553 (Functional Straight Lapel)",
          "V-4554 (Real Round Point Sham)",
          "V-4556 (Real Straight w/ Hand Stitching)",
          "V-4557 (Real Round w/ Hand Stitching)"
        ] 
      },
      { 
        name: "Collar & Lapel Fabric", 
        codes: [
          "V-465V (Collar Customer Appointed)",
          "V-465J (Collar Use Jacket Front Panel Fabric)",
          "V-4225 (Satin Collar)",
          "V-465Q (Lapel Customer Appointed Fabric)",
          "V-465U (Collar & Front 0.5cm Satin Welt)",
          "V-4653 (Customer Piping on Collar & Edge)",
          "V-4654 (Collar & Front 1.2cm Customer Welt)"
        ] 
      },
      { 
        name: "Front Buttons", 
        codes: [
          "V-4110 (3 Buttons)",
          "V-4111 (4 Buttons)",
          "V-4112 (5 Buttons)",
          "V-4113 (6 Buttons)",
          "V-4114 (7 Buttons)",
          "V-4115 (8 Buttons)",
          "V-4116 (2 Buttons)",
          "V-4117 (1 Button)",
          "V-4118 (9 Buttons)",
          "V-4119 (10 Buttons)",
          "V-411A (Double Breast 4 Buttons)",
          "V-411B (Double Breast 6 Buttons)",
          "V-411C (Double Breast 8 Buttons)",
          "V-411D (4 Buttons - Last 2 Not Fastened)",
          "V-411E (5 Buttons - Last 2 Not Fastened)",
          "V-411F (6 Buttons - Last 2 Not Fastened)"
        ] 
      },
      { 
        name: "Front Pocket Style", 
        codes: [
          "V-4200 (No Front Pocket)",
          "V-4201 (Welt Pocket)",
          "V-4202 (Double Besom)",
          "V-4203 (Double Besom w/ Flap)",
          "V-4204 (Double Besom w/ Tab & Button)",
          "V-4205 (Double Besom w/ Pointed Tab)",
          "V-4206 (Double Besom w/ Round Tab)",
          "V-4207 (Double Besom w/ Square Tab)",
          "V-4208 (Double Besom w/ Diamond Flap)",
          "V-4209 (Double Besom w/ Flap & Button)",
          "V-4210 (Slant Welt)",
          "V-4211 (Slant Double Besom)",
          "V-4220 (Patch Pocket)",
          "V-4221 (Patch w/ Button)",
          "V-4222 (Patch w/ Flap)",
          "V-4223 (Patch w/ Flap & Button)",
          "V-4260 (Large Patch Pocket)",
          "V-4261 (Patch w/ Single Pleat)",
          "V-4262 (Patch w/ Double Pleats)"
        ] 
      },
      { 
        name: "Chest Pocket", 
        codes: [
          "V-4230 (No Chest Pocket)",
          "V-4231 (Left Welt Chest Pocket)",
          "V-4232 (Left Double Besom)",
          "V-4233 (Left Patch)",
          "V-4234 (Both Sides Welt)",
          "V-4235 (Both Sides Double Besom)",
          "V-4236 (Both Sides Patch)"
        ] 
      },
      { 
        name: "Back Style", 
        codes: [
          "V-4300 (Same Fabric as Front)",
          "V-4301 (Lining Back)",
          "V-4302 (Half & Half - Top Fabric, Bottom Lining)",
          "V-4303 (Half & Half - Top Lining, Bottom Fabric)",
          "V-4310 (Back w/ Buckle)",
          "V-4311 (Back w/ Elastic)",
          "V-4312 (Back w/ Belt & Buckle)",
          "V-4313 (Back w/ Martingale)",
          "V-4314 (Back w/ Cinch)",
          "V-4320 (Back w/ Pleat)",
          "V-4321 (Back w/ Center Vent)",
          "V-4322 (Back w/ Side Vents)"
        ] 
      },
      { 
        name: "Back Buckle Style", 
        codes: [
          "V-4350 (No Buckle)",
          "V-4351 (Standard Buckle)",
          "V-4352 (D-Ring Buckle)",
          "V-4353 (Slide Buckle)",
          "V-4354 (Fabric Tie)",
          "V-4355 (Elastic Side Tab)",
          "V-4356 (Button Adjustment)"
        ] 
      },
      { 
        name: "Bottom Style", 
        codes: [
          "V-4400 (Pointed Bottom)",
          "V-4401 (Straight Bottom)",
          "V-4402 (Round Bottom)",
          "V-4403 (Square Bottom)",
          "V-4404 (Asymmetric Bottom)",
          "V-4405 (Extended Front)",
          "V-4406 (Cutaway Front)"
        ] 
      },
      { 
        name: "Edge & Pic Stitch", 
        codes: [
          "V-4501 (No Pic Stitch)",
          "V-4502 (0.1cm Pic Stitch)",
          "V-4503 (0.3cm Pic Stitch)",
          "V-4504 (0.6cm Pic Stitch)",
          "V-4505 (Hand Pic Stitch)",
          "V-4506 (Double Needle Top Stitch)",
          "V-4507 (Piped Edge)",
          "V-4508 (Contrast Piping)"
        ] 
      },
      { 
        name: "Lining Style", 
        codes: [
          "V-4600 (Full Lining)",
          "V-4601 (Half Lining)",
          "V-4602 (No Lining)",
          "V-4603 (Contrast Lining)",
          "V-4604 (Printed Lining)"
        ] 
      },
      { 
        name: "Inner Pocket", 
        codes: [
          "V-4610 (No Inner Pocket)",
          "V-4611 (Left Inner Pocket)",
          "V-4612 (Right Inner Pocket)",
          "V-4613 (Both Inner Pockets)",
          "V-4614 (Inner Pocket w/ Zipper)",
          "V-4615 (Inner Pocket w/ Button)"
        ] 
      },
      { 
        name: "Button Style", 
        codes: [
          "V-4700 (Horn Buttons)",
          "V-4701 (Corozo Buttons)",
          "V-4702 (Mother of Pearl)",
          "V-4703 (Plastic Buttons)",
          "V-4704 (Metal Buttons)",
          "V-4705 (Fabric Covered)",
          "V-4706 (Contrast Buttons)"
        ] 
      },
      { 
        name: "Labels & Monogram", 
        codes: [
          "V-46D0 (No Name Label)",
          "V-46D1 (Name Label Same Level Left Armhole)",
          "V-46D2 (Name Label Same Level Right Armhole)",
          "V-46D5 (Customer Name Embroidery Left Lining)",
          "V-46C1 (Embroider in Left Lining)",
          "V-46C2 (Embroider in Right Lining)",
          "V-46C3 (Brand Label 3cm Under Back Collar)",
          "V-46E1 (Excess Fabric Return)",
          "V-46F1 (Back Length Under Waist)",
          "V-46F5 (Using Reversed Side of Fabric)"
        ] 
      }
    ]
  },
  overcoat: {
    label: "Overcoat",
    icon: "🧥",
    pdfUrl: STYLE_PDF_URLS.overcoat,
    categories: [
      { 
        name: "Canvas Construction", 
        codes: [
          "D-000A (Full Canvas)",
          "D-000B (Half Canvas)",
          "D-00C1 (Fused)",
          "D-00C2 (Middle-End Canvas)",
          "D-0AAA (Fully Hand Made - Full Canvas)",
          "D-00AA (Semi Hand Made - Full Canvas)",
          "D-0AAB (Fully Hand Made - Half Canvas)",
          "D-OBAA (Semi Hand Made - Half Canvas)",
          "D-00D1 (No Chest Canvas)"
        ] 
      },
      { 
        name: "Lapel Style", 
        codes: [
          "D-6001 (Notch Lapel)",
          "D-6002 (Peak Lapel)",
          "D-6003 (Semi-Notch Lapel)",
          "D-6004 (Semi-Peak Lapel)",
          "D-6005 (Shawl Lapel)",
          "D-6006 (Fish Mouth Lapel)",
          "D-6007 (Closed Collar)",
          "D-6009 (Stand Collar w/ Collar Band)",
          "D-601U (Duck Mouth Lapel)",
          "D-600R (Lower Arc Peak Lapel)",
          "D-6699 (Peak w/ Middle Splice - Customer Fabric)"
        ] 
      },
      { 
        name: "Collar & Lapel Corner", 
        codes: [
          "D-6531 (Normal Small Round Corner)",
          "D-6533 (Square Corner)"
        ] 
      },
      { 
        name: "Lapel Dart", 
        codes: [
          "D-60G1 (One Dart Under Lapel)",
          "D-60G2 (Double Darts Under Lapel)"
        ] 
      },
      { 
        name: "Lapel Buttonhole Position", 
        codes: [
          "D-6541 (Left Sham Buttonhole)",
          "D-6542 (Right Sham Buttonhole)",
          "D-6543 (Both Sides Sham)",
          "D-6544 (No Lapel Buttonhole)",
          "D-6545 (Double Left Sham)",
          "D-6546 (Double Right Sham)",
          "D-6547 (Real Buttonhole Right)",
          "D-6548 (Real Buttonhole Both Sides)",
          "D-6549 (3 Real Left, 2 Real Right)",
          "D-6550 (Three Left Sham)",
          "D-605Z (L&R Three Sham Divided Equally)",
          "D-654Y (3 Left Sham, 1 Right Sham)"
        ] 
      },
      { 
        name: "Lapel Buttonhole Style", 
        codes: [
          "D-655K (Real Straight 0.5cm)",
          "D-6551 (Normal Straight)",
          "D-6552 (Normal Round Point)",
          "D-6553 (Functional Straight)",
          "D-6554 (Real Round Point)",
          "D-6556 (Real Straight w/ Temp Stitch)",
          "D-6557 (Real Round w/ Temp Stitch)"
        ] 
      },
      { 
        name: "Lapel Buttonhole Length", 
        codes: [
          "D-65AE (2.0cm)",
          "D-6562 (2.4cm)",
          "D-6563 (2.7cm)",
          "D-6564 (3.0cm)"
        ] 
      },
      { 
        name: "Collar Fabric", 
        codes: [
          "D-6651 (Satin Collar)",
          "D-665P (1cm Satin Rim on Collar)",
          "D-665S (1.2cm Satin Rim on Collar)",
          "D-665V (Collar Surface Customer Appointed)",
          "D-6X0P (Collar Face Same as Body)"
        ] 
      },
      { 
        name: "Collar Felt Style", 
        codes: [
          "D-6671 (Match Fabric)",
          "D-6672 (Customer Appointed)",
          "D-6673 (Fabric Collar Felt)",
          "D-6679 (Fabric Felt w/ Customer Back Side)",
          "D-69.J1 (Customer Fabric Back Side)"
        ] 
      },
      { 
        name: "Front Buttons - Single Breast", 
        codes: [
          "D-6011 (SB 1 Button)",
          "D-6012 (SB 2 Buttons)",
          "D-6013 (SB 3 Buttons)",
          "D-6014 (SB 4 Buttons)",
          "D-6023 (SB 5 Buttons)",
          "D-6020 (SB 6 Buttons)",
          "D-602B (SB 3 Buttons - Roll to 2nd)",
          "D-602C (SB 3 Buttons - Roll Middle 1st & 2nd)",
          "D-602D (SB 4 Buttons - Roll to 2nd)",
          "D-602E (SB 4 Buttons - End Between 1st & 2nd)",
          "D-602M (SB 6 Buttons)",
          "D-602S (SB 6 Buttons)",
          "D-6A15 (SB 4 Buttons)"
        ] 
      },
      { 
        name: "Front Buttons - Double Breast", 
        codes: [
          "D-6015 (DB 4 Buttons - 2 Fasten)",
          "D-6016 (DB 6 Buttons - 2 Fasten)",
          "D-6017 (DB 4 Buttons - 1 Fasten)",
          "D-6018 (DB 6 Buttons - 1 Fasten)",
          "D-6019 (DB 6 Buttons - 3 Fasten)",
          "D-6027 (DB 6 Buttons - 2 Fasten)",
          "D-6028 (DB 2 Buttons - 1 Fasten)",
          "D-6029 (DB 8 Buttons - 3 Fasten)",
          "D-6037 (DB 4 Buttons - 1 Fasten)",
          "D-601Q (DB 6 Buttons - 3 Fasten)",
          "D-602A (DB 10 Buttons - 3 Fasten)",
          "D-602K (DB 10 Buttons - 4 Fasten)"
        ] 
      },
      { 
        name: "Lapel Width", 
        codes: [
          "60W1 (5.0cm)", "60W4 (6.0cm)", "60W6 (6.5cm)", "60W7 (7.0cm)",
          "60W8 (7.5cm)", "60W9 (7.6cm)", "60S4 (8.0cm)", "60S5 (4.0cm)",
          "60S6 (5.3cm)", "60S8 (5.5cm)", "60S9 (7.5cm)", "60SD (6.0cm)",
          "60X1 (8.5cm)", "60X7 (8.1cm)", "60X8 (8.4cm)", "60X9 (8.8cm)",
          "60N1 (7.9cm)", "60N2 (8.3cm)", "60N3 (8.6cm)", "60N4 (8.9cm)",
          "60N5 (9.0cm)", "60N6 (9.5cm)", "60N7 (9.3cm)", "60N8 (10.2cm)",
          "60N9 (10.0cm)", "60NA (10.1cm)", "60NC (9.1cm)",
          "60S1 (10.8cm)", "60S2 (11.0cm)", "60SC (11.2cm)", "60SE (11.1cm)",
          "60SF (10.5cm)", "60Y1 (9.4cm)", "60Y2 (9.6cm)", "60Y3 (9.7cm)",
          "60Y4 (9.9cm)", "60Y5 (13.0cm)", "60Y6 (11.4cm)", "60Y7 (12.7cm)",
          "60Y8 (14.0cm)", "60Y9 (12.3cm)", "60YE (12.1cm)"
        ] 
      },
      { 
        name: "Front Buttonhole Style", 
        codes: [
          "D-6640 (Normal Buttoning)",
          "D-6641 (Anchor Buttons)",
          "D-6642 (Slant Buttonhole)",
          "D-664Q (No 1st Front Button & Hole)",
          "D-664W (No Holes or Buttons - Send w/ Order)"
        ] 
      },
      { 
        name: "Chest Pocket", 
        codes: [
          "D-6100 (No Chest Pocket)",
          "D-6101 (Normal Chest Pocket)",
          "D-6102 (Arc Chest Pocket)",
          "D-6103 (Ship Shape)",
          "D-6110 (Double Besom)",
          "D-6111 (Double Besom w/ Flap)",
          "D-6112 (Double Besom w/ Button)",
          "D-6120 (Patch Pocket)",
          "D-6121 (Patch w/ Flap)",
          "D-6122 (Patch w/ Button)"
        ] 
      },
      { 
        name: "Lower Pocket Style", 
        codes: [
          "D-6200 (No Lower Pocket)",
          "D-6201 (Flap Pocket)",
          "D-6202 (Patch Pocket)",
          "D-6203 (Double Besom)",
          "D-6204 (Double Besom w/ Flap)",
          "D-6205 (Double Besom w/ Button)",
          "D-6210 (Slant Pocket)",
          "D-6211 (Slant Double Besom)",
          "D-6212 (Slant w/ Flap)",
          "D-6220 (Patch w/ Flap)",
          "D-6221 (Patch w/ Button)",
          "D-6222 (Patch w/ Pleat)",
          "D-6230 (Bellows Pocket)",
          "D-6240 (Patch w/ Box Pleat)",
          "D-6250 (Inverted Pleat Pocket)"
        ] 
      },
      { 
        name: "Ticket Pocket", 
        codes: [
          "D-6300 (No Ticket Pocket)",
          "D-6301 (Right Ticket Pocket)",
          "D-6302 (Left Ticket Pocket)",
          "D-6303 (Ticket w/ Flap)",
          "D-6304 (Ticket Double Besom)",
          "D-6305 (Ticket w/ Button)"
        ] 
      },
      { 
        name: "Vent Style", 
        codes: [
          "D-6400 (No Vent)",
          "D-6401 (Center Vent)",
          "D-6402 (Side Vents)",
          "D-6403 (Inverted Pleat Vent)",
          "D-6404 (Hook Vent)"
        ] 
      },
      { 
        name: "Vent Adjustments", 
        codes: [
          "D-64K1 (-2cm on Vent)",
          "D-64K2 (-4cm on Vent)",
          "D-64K3 (-6cm on Vent)",
          "D-64K4 (+2cm on Vent)",
          "D-64K5 (+4cm on Vent)",
          "D-64K6 (+6cm on Vent)",
          "D-64K7 (-8cm on Vent)",
          "D-64K8 (-10cm on Vent)"
        ] 
      },
      { 
        name: "Length", 
        codes: [
          "D-6500 (Short Length - Above Knee)",
          "D-6501 (Mid Length - At Knee)",
          "D-6502 (Long Length - Below Knee)",
          "D-6503 (Full Length - Mid Calf)",
          "D-6504 (Maxi Length - Ankle)"
        ] 
      },
      { 
        name: "Back Length Adjustment", 
        codes: [
          "D-6931 (+0.5cm Half Back to Bottom)",
          "D-6932 (+1.0cm Half Back to Bottom)",
          "D-6933 (-0.5cm Half Back to Bottom)",
          "D-6934 (-1.0cm Half Back to Bottom)"
        ] 
      },
      { 
        name: "Lining Style", 
        codes: [
          "D-6601 (Full Lining)",
          "D-6602 (Half Lining)",
          "D-6603 (Quarter Lining)",
          "D-6604 (Removable Lining)",
          "D-6605 (Quilted Lining)"
        ] 
      },
      { 
        name: "Shoulder Style", 
        codes: [
          "D-6701 (Natural Shoulder)",
          "D-6702 (Padded Shoulder)",
          "D-6703 (Extended Shoulder)",
          "D-6704 (Raglan Shoulder)",
          "D-6705 (Split Raglan)"
        ] 
      },
      { 
        name: "Sleeve Style", 
        codes: [
          "D-6801 (Standard Sleeve)",
          "D-6802 (Raglan Sleeve)",
          "D-6803 (Set-In Sleeve)",
          "D-6804 (Epaulet Tab)"
        ] 
      },
      { 
        name: "Sleeve Cuff Adjustment", 
        codes: [
          "D-6955 (Cuff +1.0cm)",
          "D-6956 (Cuff +2.0cm)",
          "D-6957 (Cuff -1.0cm)",
          "D-6958 (Cuff -2.0cm)"
        ] 
      },
      { 
        name: "Inner Pocket - Left Breast", 
        codes: [
          "D-6880 (No Left Inner Pocket)",
          "D-6881 (Standard Left Inner)",
          "D-6882 (Left Inner w/ Zipper)",
          "D-6883 (Left Inner w/ Button)",
          "D-6884 (Left Inner w/ Tab)",
          "D-68L1 (Left Patch Inner)",
          "D-68L2 (Left Patch w/ Button)",
          "D-68L3 (Left Patch w/ Tab)"
        ] 
      },
      { 
        name: "Inner Pocket - Right Breast", 
        codes: [
          "D-68R0 (No Right Inner Pocket)",
          "D-68R1 (Standard Right Inner)",
          "D-68R2 (Right Inner w/ Zipper)",
          "D-68R3 (Right Inner w/ Round Tab & Button)",
          "D-68R5 (Right Inner w/ Diamond Flap Button)",
          "D-68R6 (Right Inner w/ Flap Button)",
          "D-69L0 (Right Patch Inner)",
          "D-69L1 (Right Patch w/ Flap Button)",
          "D-69L2 (Right Patch w/ Button)",
          "D-69L3 (Right Patch w/ Tab Button)",
          "D-69L4 (Right Patch w/ Square Tab Button)",
          "D-69L5 (Right Patch w/ Round Tab Button)",
          "D-69L6 (Right Patch w/ Normal Flap Button)"
        ] 
      },
      { 
        name: "Pen Pocket", 
        codes: [
          "D-6841 (Left Pen Pocket)",
          "D-6842 (Right Pen Pocket)",
          "D-6843 (Both Pen Pockets)"
        ] 
      },
      { 
        name: "Name Card Pocket", 
        codes: [
          "D-6846 (Right Lower Point Tab 25x24cm)",
          "D-684L (Point Tape Button Right 25x24cm)",
          "D-684N (L&R Big Name Card Point Tab Button)",
          "D-684Y (L&R Patch Name Card Pocket)",
          "D-684A (Inner Flap w/ Body Lining Edge Piping)",
          "D-68N5 (Round Tab Button Right 25x24cm)",
          "D-68N8 (Square Tab Button Right 25x24cm)",
          "D-68N9 (L&R Big Name Card Square Tab Button)"
        ] 
      },
      { 
        name: "MP3 Pocket", 
        codes: [
          "D-6847 (MP3 Pocket)",
          "D-686D (MP3 Pocket Color - Customer Appoint)"
        ] 
      },
      { 
        name: "Inner Pocket Bartack", 
        codes: [
          "D-6860 (No Bartack)",
          "D-6861 (I Shape Bartack)",
          "D-6862 (D Shape Bartack)",
          "D-6863 (Dot Bartack)",
          "D-6864 (X Bartack)"
        ] 
      },
      { 
        name: "Label Position - Fabric", 
        codes: [
          "D-687H (No Fabric Label)",
          "D-6872 (Below Right Inner Pocket 10cm)",
          "D-6877 (5cm Below Left Pen Pocket)",
          "D-687C (2cm Above Left Inner Pocket)",
          "D-6879 (2cm Above Right Inner Pocket)",
          "D-687F (Above Left Name Card Pocket)",
          "D-687G (Below Left Name Card Pocket)",
          "D-68M1 (Above Right Name Card Pocket)",
          "D-68M2 (Below Right Name Card Pocket)"
        ] 
      },
      { 
        name: "Label Position - Brand", 
        codes: [
          "D-6870 (No Brand Label)",
          "D-6871 (5cm Below Left Pen Pocket)",
          "D-6875 (Under Left Business Card Pocket)",
          "D-6876 (2cm Above Right Inner Pocket)",
          "D-687S (10cm Below Right Inner Breast)",
          "D-68S1 (Above Left Business Card Pocket)",
          "D-68S2 (2cm Above Left Inner Breast)",
          "D-68S3 (Above Right Name Card Pocket)"
        ] 
      },
      { 
        name: "Label Position - Name", 
        codes: [
          "D-687A (No Name Label)",
          "D-6873 (2cm Above Left Inner Breast)",
          "D-6878 (10cm Below Right Inner Pocket)",
          "D-687B (2cm Above Right Inner Pocket)",
          "D-68K1 (10cm Below Left Inner Pocket)",
          "D-68K2 (Above Left Name Card Pocket)",
          "D-68K3 (Below Left Name Card Pocket)",
          "D-68K4 (Above Right Name Card Pocket)",
          "D-68K5 (Below Right Name Card Pocket)"
        ] 
      },
      { 
        name: "Collar Tab Style", 
        codes: [
          "D-6881 (Silk Hanger Loop Above Collar)",
          "D-6882 (Blank Silk Tab Below Collar)",
          "D-6883 (Marked Tab on Collar)",
          "D-6884 (Marked Tab Below Collar)",
          "D-6885 (No Collar Tab)",
          "D-6887 (Customer Supplier Loop Above)",
          "D-6888 (Customer Supplier Loop Under)",
          "D-688A (Fabric Tip Above Collar)",
          "D-688B (Lining Tab on Collar)",
          "D-688G (Fabric Tip Below Collar)",
          "D-688H (Lining Tab Below Collar)",
          "D-688J (Special Fabric Hanger Loop Above)",
          "D-688K (Customer Fabric Loop Under Collar)"
        ] 
      },
      { 
        name: "Care Label Location", 
        codes: [
          "D-687N (No Care Label)",
          "D-687P (Care Label in Left Inner Pocket)",
          "D-687R (Care Label Sideseam 5cm Under Armhole)",
          "D-687T (Care Label 5cm Under Left Armhole)"
        ] 
      },
      { 
        name: "Shipments & Accessories", 
        codes: [
          "D-6950 (Return Unspent Fabric)",
          "D-6953 (Send 5x5cm Fabric Sample)",
          "D-6954 (Extra Buttons & Thread in Envelope)"
        ] 
      },
      { 
        name: "Handmade Pic Stitch", 
        codes: [
          "D-68U9 (Handmade Pic Stitch)"
        ] 
      }
    ]
  }
};

const AdminRCCodeBookPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("styles"); // styles, ai
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedGarment, setSelectedGarment] = useState("jacket");
  
  // PDF Viewer state
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = useState(null);
  
  // Image viewer state
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [currentImagePages, setCurrentImagePages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentCategoryName, setCurrentCategoryName] = useState("");
  const [extractedImages, setExtractedImages] = useState({}); // Cache for extracted images
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  
  // AI Analysis state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  const [cart, setCart] = useState({ items: [] });
  const [cartOpen, setCartOpen] = useState(false);

  // Cart bootstrap + cross-tab sync
  useEffect(() => {
    setCart(getStyleCart());
    return subscribeStyleCart(setCart);
  }, []);

  const handleAddToCart = (rawCode) => {
    // rawCode is the original row string like "C-0001 (Notch Lapel)"
    const m = rawCode.match(/^([A-Z]-[A-Z0-9]+)\s*(?:\(([^)]+)\))?/);
    const codeOnly = m ? m[1] : rawCode;
    const label    = m && m[2] ? m[2] : rawCode;
    const mapping  = codeToFieldValue(codeOnly);
    if (!mapping) {
      toast.warning(`${codeOnly}: no dropdown mapping yet — will be ignored when applied. Tell me which field this should target.`);
      return;
    }
    addToStyleCart({ code: codeOnly, label, field: mapping.field, value: mapping.value });
    setCartOpen(true);
    toast.success(`Added ${codeOnly}: ${label}`);
  };

  const sendCartTo = (route) => {
    if (!cart.items.length) {
      toast.error("Cart is empty");
      return;
    }
    // Keep the cart in localStorage so the destination page can consume it
    toast.success(`Sending ${cart.items.length} code${cart.items.length === 1 ? "" : "s"} to ${route === "/admin/quick-order" ? "Quick Order" : "POS"}…`);
    navigate(route);
  };
  const fileInputRef = useRef(null);

  // Body Posture Analysis state (4 images)
  const [postureImages, setPostureImages] = useState([null, null, null, null]); // Front, Left, Right, Back
  const [isAnalyzingPosture, setIsAnalyzingPosture] = useState(false);
  const [postureResult, setPostureResult] = useState(null);

  // Open PDF viewer
  const openPdfViewer = (url) => {
    setPdfViewerUrl(url);
    setShowPdfViewer(true);
  };
  
  // Extract image from PDF
  const extractPdfImage = async (garment, pageNum) => {
    const cacheKey = `${garment}_${pageNum}`;
    
    // Check cache first
    if (extractedImages[cacheKey]) {
      return extractedImages[cacheKey];
    }
    
    try {
      const pdfUrl = STYLE_PDF_URLS[garment];
      if (!pdfUrl) {
        throw new Error(`No PDF URL for garment: ${garment}`);
      }
      
      const response = await axios.post(`${API}/admin/rc-codebook/extract-page`, {
        pdf_url: pdfUrl,
        page_number: pageNum,
        scale: 2.0
      });
      
      if (response.data.success) {
        // Cache the image
        setExtractedImages(prev => ({
          ...prev,
          [cacheKey]: response.data.image
        }));
        return response.data.image;
      }
      throw new Error("Failed to extract image");
    } catch (error) {
      console.error(`Error extracting page ${pageNum}:`, error);
      throw error;
    }
  };
  
  // Open image viewer for a category
  const openImageViewer = async (categoryName) => {
    const mapping = STYLE_PAGE_MAPPING[selectedGarment];
    if (mapping && mapping.categories[categoryName]) {
      const pages = mapping.categories[categoryName].pages;
      setCurrentImagePages(pages);
      setCurrentImageIndex(0);
      setCurrentCategoryName(categoryName);
      setShowImageViewer(true);
      setIsLoadingImage(true);
      
      // Pre-load the first image
      try {
        await extractPdfImage(selectedGarment, pages[0]);
      } catch (error) {
        toast.error("Failed to load image from PDF");
      } finally {
        setIsLoadingImage(false);
      }
    }
  };
  
  // Get image for current page (from cache or load)
  const getCurrentImage = () => {
    if (currentImagePages.length === 0) return null;
    const pageNum = currentImagePages[currentImageIndex];
    const cacheKey = `${selectedGarment}_${pageNum}`;
    return extractedImages[cacheKey];
  };
  
  // Load image when changing pages
  const loadCurrentImage = async () => {
    if (currentImagePages.length === 0) return;
    const pageNum = currentImagePages[currentImageIndex];
    const cacheKey = `${selectedGarment}_${pageNum}`;
    
    if (!extractedImages[cacheKey]) {
      setIsLoadingImage(true);
      try {
        await extractPdfImage(selectedGarment, pageNum);
      } catch (error) {
        console.error("Error loading image:", error);
      } finally {
        setIsLoadingImage(false);
      }
    }
  };
  
  // Navigate images
  const nextImage = async () => {
    if (currentImageIndex < currentImagePages.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };
  
  const prevImage = async () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };
  
  // Load image when index changes
  useEffect(() => {
    if (showImageViewer && currentImagePages.length > 0) {
      loadCurrentImage();
    }
  }, [currentImageIndex, showImageViewer]);

  // Filter style codes based on search
  const filteredCategories = useMemo(() => {
    const garmentData = STYLE_CATEGORIES[selectedGarment];
    if (!searchQuery) return garmentData.categories;
    
    const query = searchQuery.toLowerCase();
    return garmentData.categories
      .map(category => ({
        ...category,
        codes: category.codes.filter(code => 
          code.toLowerCase().includes(query)
        )
      }))
      .filter(category => category.codes.length > 0);
  }, [selectedGarment, searchQuery]);

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        setSelectedImage({ base64, type: file.type, name: file.name });
        setImagePreview(reader.result);
        setAnalysisResult(null);
      };
      reader.readAsDataURL(file);
    }
  };

  // Analyze image with AI
  const analyzeImage = async () => {
    if (!selectedImage) {
      toast.error("Please select an image first");
      return;
    }

    setIsAnalyzing(true);
    setAnalysisResult(null);

    try {
      const response = await axios.post(`${API}/admin/analyze-style-codes`, {
        image_base64: selectedImage.base64,
        image_type: selectedImage.type
      });

      if (response.data.success) {
        setAnalysisResult({
          garmentType: response.data.garment_type,
          styleCodes: response.data.style_codes,
          rawAnalysis: response.data.raw_analysis,
          confidence: response.data.confidence
        });
        toast.success("Analysis complete!");
      } else {
        toast.error("Analysis failed. Please try again.");
      }
    } catch (error) {
      console.error("Analysis error:", error);
      toast.error("Failed to analyze image. Please try again.");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Copy code to clipboard
  const copyCode = async (code) => {
    const codeOnly = code.split(' ')[0];
    try {
      await navigator.clipboard.writeText(codeOnly);
      setCopiedCode(codeOnly);
      toast.success(`Copied: ${codeOnly}`);
    } catch (err) {
      // Fallback for browsers without clipboard permission
      const textArea = document.createElement('textarea');
      textArea.value = codeOnly;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopiedCode(codeOnly);
        toast.success(`Copied: ${codeOnly}`);
      } catch (fallbackErr) {
        toast.error('Copy failed - please copy manually');
      }
      document.body.removeChild(textArea);
    }
    setTimeout(() => setCopiedCode(null), 2000);
  };

  // Count total codes for selected garment
  const totalCodes = useMemo(() => {
    return STYLE_CATEGORIES[selectedGarment].categories.reduce(
      (total, cat) => total + cat.codes.length, 0
    );
  }, [selectedGarment]);

  return (
    <div className="min-h-screen bg-[#0a1628] text-white">
      {/* Header */}
      <header className="bg-[#111c2e] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/admin")} className="text-slate-400 hover:text-white">
                <ArrowLeft size={24} />
              </button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Book size={24} className="text-blue-400" />
                  RC Code Book
                </h1>
                <p className="text-sm text-slate-400">Complete Style Code Reference</p>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab("styles")}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                activeTab === "styles"
                  ? "bg-blue-500 text-white"
                  : "bg-[#0a1628] text-slate-400 hover:text-white"
              }`}
            >
              <Shirt size={16} />
              Style Codes
            </button>
            <button
              onClick={() => setActiveTab("ai")}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                activeTab === "ai"
                  ? "bg-gradient-to-r from-purple-500 to-blue-500 text-white"
                  : "bg-[#0a1628] text-slate-400 hover:text-white"
              }`}
            >
              <Sparkles size={16} />
              AI Style Analysis
            </button>
            <button
              onClick={() => setActiveTab("posture")}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all flex items-center gap-2 ${
                activeTab === "posture"
                  ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white"
                  : "bg-[#0a1628] text-slate-400 hover:text-white"
              }`}
            >
              <User size={16} />
              AI Body Posture
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Style Codes Tab */}
        {activeTab === "styles" && (
          <div>
            {/* Garment Type Selector with View PDF button */}
            <div className="flex flex-col lg:flex-row gap-4 mb-6">
              <div className="flex gap-2 overflow-x-auto pb-2 flex-1">
                {Object.entries(STYLE_CATEGORIES).map(([key, data]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setSelectedGarment(key);
                      setExpandedCategory(null);
                      setSearchQuery("");
                    }}
                    className={`px-4 py-3 rounded-xl font-medium text-sm transition-all flex items-center gap-2 whitespace-nowrap ${
                      selectedGarment === key
                        ? "bg-blue-500 text-white"
                        : "bg-[#111c2e] text-slate-400 hover:text-white border border-white/10"
                    }`}
                  >
                    <span className="text-xl">{data.icon}</span>
                    {data.label}
                  </button>
                ))}
              </div>
              
              {/* View Style Images Button */}
              <Button
                onClick={() => openPdfViewer(STYLE_CATEGORIES[selectedGarment].pdfUrl)}
                className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 whitespace-nowrap"
              >
                <Eye size={16} className="mr-2" />
                View {STYLE_CATEGORIES[selectedGarment].label} Style Images
              </Button>
            </div>

            {/* Search Bar */}
            <div className="bg-[#111c2e] rounded-xl p-4 border border-white/10 mb-6">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder={`Search ${STYLE_CATEGORIES[selectedGarment].label} style codes...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-[#0a1628] border-white/10"
                />
              </div>
              <p className="text-sm text-slate-400 mt-3">
                {searchQuery 
                  ? `Found ${filteredCategories.reduce((t, c) => t + c.codes.length, 0)} codes matching "${searchQuery}"`
                  : `${totalCodes} style codes in ${STYLE_CATEGORIES[selectedGarment].categories.length} categories`
                }
              </p>
            </div>

            {/* Embedded PDF Preview - Inline */}
            <div className="mb-6 bg-[#111c2e] rounded-xl border border-purple-500/30 overflow-hidden">
              <div className="flex items-center justify-between p-3 bg-purple-500/10 border-b border-purple-500/20">
                <div className="flex items-center gap-2">
                  <FileText size={18} className="text-purple-400" />
                  <span className="font-medium text-sm">{STYLE_CATEGORIES[selectedGarment].label} Style Guide with Images</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => openPdfViewer(STYLE_CATEGORIES[selectedGarment].pdfUrl)}
                    className="text-purple-400 hover:text-white"
                  >
                    <Maximize2 size={14} className="mr-1" />
                    Fullscreen
                  </Button>
                  <a
                    href={STYLE_CATEGORIES[selectedGarment].pdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-purple-400 hover:text-white px-2"
                  >
                    <ExternalLink size={14} />
                    Open PDF
                  </a>
                </div>
              </div>
              <iframe
                src={`${STYLE_CATEGORIES[selectedGarment].pdfUrl}#toolbar=0`}
                className="w-full h-[400px] bg-white"
                title={`${STYLE_CATEGORIES[selectedGarment].label} Style Guide`}
              />
            </div>

            {/* Style Categories */}
            <div className="space-y-4">
              {filteredCategories.map((category, index) => {
                const pageMapping = STYLE_PAGE_MAPPING[selectedGarment]?.categories[category.name];
                const hasImages = pageMapping && pageMapping.pages && pageMapping.pages.length > 0;
                const firstPage = hasImages ? pageMapping.pages[0] : null;
                
                return (
                <div
                  key={category.name || `cat-${index}`}
                  className="bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden"
                >
                  {/* Category Header with Image Preview */}
                  <div className="flex items-stretch">
                    {/* Thumbnail Preview - Now shows icon placeholder */}
                    {hasImages && (
                      <button
                        onClick={() => openImageViewer(category.name)}
                        className="w-24 min-h-[80px] bg-purple-500/10 border-r border-white/10 flex flex-col items-center justify-center hover:bg-purple-500/20 transition-colors group relative overflow-hidden"
                      >
                        <ImageIcon size={24} className="text-purple-400 mb-1" />
                        <span className="text-[10px] text-purple-300">P.{firstPage}</span>
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Eye size={20} className="text-white" />
                        </div>
                      </button>
                    )}
                    
                    {/* Category Info */}
                    <button
                      className="flex-1 p-4 flex items-center justify-between hover:bg-white/5 transition-colors text-left"
                      onClick={() => setExpandedCategory(expandedCategory === category.name ? null : category.name)}
                    >
                      <div className="flex-1">
                        <span className="font-medium">{category.name}</span>
                        {pageMapping?.description && (
                          <p className="text-xs text-slate-500 mt-0.5">{pageMapping.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {hasImages && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openImageViewer(category.name);
                            }}
                            className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1 px-2 py-1 rounded bg-purple-500/10 hover:bg-purple-500/20"
                          >
                            <ImageIcon size={12} />
                            {pageMapping.pages.length} {pageMapping.pages.length === 1 ? 'image' : 'images'}
                          </button>
                        )}
                        <span className="text-xs text-slate-400 bg-slate-700/50 px-2 py-1 rounded-full">{category.codes.length} codes</span>
                        {expandedCategory === category.name ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </div>
                    </button>
                  </div>

                  {expandedCategory === category.name && (
                    <div className="border-t border-white/10">
                      {/* Image Preview Banner */}
                      {hasImages && (
                        <div 
                          className="p-3 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-b border-white/10 cursor-pointer hover:from-purple-500/15 hover:to-blue-500/15 transition-colors"
                          onClick={() => openImageViewer(category.name)}
                        >
                          <div className="flex items-center gap-3 overflow-x-auto pb-1">
                            {pageMapping.pages.slice(0, 4).map((pageNum, idx) => (
                              <div
                                key={idx}
                                className="h-16 w-16 flex flex-col items-center justify-center bg-white/5 rounded border border-white/20 hover:border-purple-400 transition-colors"
                              >
                                <ImageIcon size={16} className="text-purple-400" />
                                <span className="text-[10px] text-slate-400 mt-1">P.{pageNum}</span>
                              </div>
                            ))}
                            {pageMapping.pages.length > 4 && (
                              <div className="h-16 w-16 flex items-center justify-center bg-white/5 rounded border border-white/20 text-sm text-slate-400">
                                +{pageMapping.pages.length - 4}
                              </div>
                            )}
                            <span className="text-xs text-purple-400 ml-2">Click to extract images from PDF →</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Codes Grid */}
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {category.codes.map((code, codeIndex) => {
                        // Each entry is "CODE (Description)" — split into the two parts
                        // so we can show description on the left and code as an amber badge on the right.
                        const m = code.match(/^(\S+)\s*(?:\((.+)\))?$/);
                        const codeOnly = m ? m[1] : code;
                        const description = m && m[2] ? m[2] : "";
                        return (
                          <div
                            key={codeIndex}
                            className="flex items-center justify-between bg-[#0a1628] rounded-lg p-3 hover:bg-[#0d1f35] transition-colors gap-2"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="text-sm text-slate-200 truncate" title={description}>
                                {description || codeOnly}
                              </span>
                              {description && (
                                <code className="text-[10px] font-mono text-amber-400/80 shrink-0">{codeOnly}</code>
                              )}
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-[10px] hover:bg-amber-500/20 text-amber-400"
                                onClick={() => handleAddToCart(code)}
                                data-testid={`rc-add-${codeOnly}`}
                                title="Add to style cart"
                              >
                                <Plus size={12} className="mr-0.5" /> Add
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 hover:bg-blue-500/20"
                                onClick={() => copyCode(code)}
                                title="Copy code"
                              >
                                {copiedCode === codeOnly ? (
                                  <Check size={14} className="text-green-400" />
                                ) : (
                                  <Copy size={14} />
                                )}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    </div>
                  )}
                </div>
              );
              })}
            </div>

            {/* PDF Reference Links - Quick Access */}
            <div className="mt-8 bg-[#111c2e] rounded-xl p-4 border border-white/10">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <Book size={18} className="text-blue-400" />
                Quick Access - All Style Books
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button 
                  onClick={() => openPdfViewer(STYLE_PDF_URLS.jacket)}
                  className="bg-[#0a1628] rounded-lg p-3 text-center hover:bg-blue-500/20 transition-colors border border-transparent hover:border-blue-500/30"
                >
                  <span className="text-2xl">🧥</span>
                  <p className="text-sm mt-1">Jacket Styles</p>
                  <p className="text-xs text-slate-500 mt-1">View Images</p>
                </button>
                <button 
                  onClick={() => openPdfViewer(STYLE_PDF_URLS.pants)}
                  className="bg-[#0a1628] rounded-lg p-3 text-center hover:bg-blue-500/20 transition-colors border border-transparent hover:border-blue-500/30"
                >
                  <span className="text-2xl">👖</span>
                  <p className="text-sm mt-1">Pant Styles</p>
                  <p className="text-xs text-slate-500 mt-1">View Images</p>
                </button>
                <button 
                  onClick={() => openPdfViewer(STYLE_PDF_URLS.vest)}
                  className="bg-[#0a1628] rounded-lg p-3 text-center hover:bg-blue-500/20 transition-colors border border-transparent hover:border-blue-500/30"
                >
                  <span className="text-2xl">🦺</span>
                  <p className="text-sm mt-1">Vest Styles</p>
                  <p className="text-xs text-slate-500 mt-1">View Images</p>
                </button>
                <button 
                  onClick={() => openPdfViewer(STYLE_PDF_URLS.overcoat)}
                  className="bg-[#0a1628] rounded-lg p-3 text-center hover:bg-blue-500/20 transition-colors border border-transparent hover:border-blue-500/30"
                >
                  <span className="text-2xl">🧥</span>
                  <p className="text-sm mt-1">Overcoat Styles</p>
                  <p className="text-xs text-slate-500 mt-1">View Images</p>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI Analysis Tab */}
        {activeTab === "ai" && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-gradient-to-br from-purple-500/10 to-blue-500/10 rounded-2xl p-6 border border-purple-500/30 mb-6">
              <div className="text-center mb-4">
                <Sparkles size={40} className="mx-auto text-purple-400 mb-3" />
                <h2 className="text-xl font-bold mb-2">AI Style Code Generator</h2>
                <p className="text-sm text-slate-400">
                  Upload a photo of a garment and AI will identify all the style codes
                </p>
              </div>

              {/* Image Upload Area */}
              <div className="mb-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/*"
                  className="hidden"
                />

                {!imagePreview ? (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-48 border-2 border-dashed border-white/20 rounded-xl flex flex-col items-center justify-center gap-3 hover:border-purple-500/50 transition-colors"
                  >
                    <Upload size={40} className="text-slate-400" />
                    <span className="text-slate-400">Click to upload image</span>
                    <span className="text-xs text-slate-500">or drag and drop</span>
                  </button>
                ) : (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full max-h-64 object-contain rounded-xl bg-black/50"
                    />
                    <button
                      onClick={() => {
                        setSelectedImage(null);
                        setImagePreview(null);
                        setAnalysisResult(null);
                      }}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500 flex items-center justify-center hover:bg-red-600"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>

              {/* Analyze Button */}
              <Button
                onClick={analyzeImage}
                disabled={!selectedImage || isAnalyzing}
                className="w-full bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 py-6 text-lg"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 size={20} className="mr-2 animate-spin" />
                    Analyzing garment...
                  </>
                ) : (
                  <>
                    <Sparkles size={20} className="mr-2" />
                    Analyze & Generate Codes
                  </>
                )}
              </Button>
            </div>

            {/* Analysis Results */}
            {analysisResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#111c2e] rounded-2xl p-6 border border-white/10"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold flex items-center gap-2">
                    <Check size={20} className="text-green-400" />
                    Analysis Results
                  </h3>
                  <span className={`text-xs px-2 py-1 rounded ${
                    analysisResult.confidence === 'high' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {analysisResult.confidence === 'high' ? '✓ High Confidence' : '⚠ Medium Confidence'}
                  </span>
                </div>

                {/* Garment Type Badge */}
                <div className="mb-4 flex items-center gap-2">
                  <span className="text-sm text-slate-400">Garment Type:</span>
                  <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium capitalize">
                    {analysisResult.garmentType || 'Unknown'}
                  </span>
                </div>

                {/* Quick Copy Codes */}
                {analysisResult.styleCodes && analysisResult.styleCodes.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium mb-2 text-slate-300">Identified Style Codes:</h4>
                    <div className="flex flex-wrap gap-2">
                      {analysisResult.styleCodes.map((item, idx) => (
                        <button
                          key={idx}
                          onClick={() => copyCode(item.code)}
                          className="px-3 py-1.5 bg-[#0a1628] border border-white/10 rounded-lg text-sm font-mono hover:border-purple-500/50 hover:bg-purple-500/10 transition-colors flex items-center gap-1"
                        >
                          {item.code}
                          {copiedCode === item.code ? (
                            <Check size={12} className="text-green-400" />
                          ) : (
                            <Copy size={12} className="text-slate-500" />
                          )}
                        </button>
                      ))}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-2 text-xs text-purple-400"
                      onClick={() => {
                        const allCodes = analysisResult.styleCodes.map(s => s.code).join(', ');
                        navigator.clipboard.writeText(allCodes);
                        toast.success("All codes copied!");
                      }}
                    >
                      <Copy size={12} className="mr-1" />
                      Copy All Codes
                    </Button>
                  </div>
                )}

                {/* Full Analysis */}
                <div className="border-t border-white/10 pt-4 mt-4">
                  <h4 className="text-sm font-medium mb-2 text-slate-300">Full Analysis:</h4>
                  <div className="bg-[#0a1628] rounded-xl p-4 whitespace-pre-wrap text-sm max-h-96 overflow-y-auto">
                    {analysisResult.rawAnalysis}
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  className="w-full mt-4"
                  onClick={() => {
                    navigator.clipboard.writeText(analysisResult.rawAnalysis);
                    toast.success("Full analysis copied to clipboard!");
                  }}
                >
                  <Copy size={16} className="mr-2" />
                  Copy Full Analysis
                </Button>
              </motion.div>
            )}

            {/* Tips */}
            <div className="mt-6 bg-[#111c2e] rounded-xl p-4 border border-white/10">
              <h4 className="font-medium mb-2 text-sm">📸 Tips for best results:</h4>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>• Use well-lit, clear photos showing the full garment</li>
                <li>• Include views of lapels, pockets, buttons, and vents</li>
                <li>• For suits, photograph jacket, pants, and vest separately</li>
                <li>• Flat lay or on-mannequin photos work best</li>
              </ul>
            </div>
          </div>
        )}

        {/* Body Posture Tab */}
        {activeTab === "posture" && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-2xl p-6 border border-amber-500/30 mb-6">
              <div className="text-center mb-6">
                <User size={40} className="mx-auto text-amber-400 mb-3" />
                <h2 className="text-xl font-bold mb-2">AI Body Posture Analysis</h2>
                <p className="text-sm text-slate-400">
                  Upload 4 photos (Front, Left Side, Right Side, Back) for comprehensive AI posture assessment
                </p>
              </div>

              {/* 4 Image Upload Slots */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                {["Front View", "Left Side", "Right Side", "Back View"].map((label, idx) => (
                  <div key={label} className="space-y-2">
                    <label className="text-xs text-slate-400 text-center block font-medium">{label}</label>
                    {postureImages[idx] ? (
                      <div className="relative group">
                        <img 
                          src={postureImages[idx].preview} 
                          alt={label}
                          className="w-full h-40 object-cover rounded-xl border-2 border-amber-500/50"
                        />
                        <button
                          onClick={() => {
                            const newImages = [...postureImages];
                            newImages[idx] = null;
                            setPostureImages(newImages);
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1.5 shadow-lg hover:bg-red-600"
                        >
                          <X size={14} />
                        </button>
                        <div className="absolute bottom-2 left-2 right-2 text-center">
                          <span className="text-xs bg-black/70 px-2 py-1 rounded text-green-400 flex items-center justify-center gap-1">
                            <CheckCheck size={12} /> Uploaded
                          </span>
                        </div>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files?.[0]) {
                              const file = e.target.files[0];
                              const reader = new FileReader();
                              reader.onloadend = () => {
                                const newImages = [...postureImages];
                                newImages[idx] = {
                                  file,
                                  preview: URL.createObjectURL(file),
                                  base64: reader.result.split(',')[1]
                                };
                                setPostureImages(newImages);
                              };
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="hidden"
                        />
                        <div className="h-40 border-2 border-dashed border-white/20 rounded-xl flex flex-col items-center justify-center hover:border-amber-500/50 transition-colors bg-[#0a1628]/50">
                          <Camera size={28} className="text-slate-500 mb-2" />
                          <span className="text-xs text-slate-500">Click to add</span>
                          <span className="text-xs text-slate-600">{label}</span>
                        </div>
                      </label>
                    )}
                  </div>
                ))}
              </div>

              {/* Bulk Upload Option */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 border-t border-white/10"></div>
                <span className="text-xs text-slate-500">or upload all 4 at once</span>
                <div className="flex-1 border-t border-white/10"></div>
              </div>

              <div className="mb-6">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const files = Array.from(e.target.files).slice(0, 4);
                    files.forEach((file, idx) => {
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        setPostureImages(prev => {
                          const newImages = [...prev];
                          newImages[idx] = {
                            file,
                            preview: URL.createObjectURL(file),
                            base64: reader.result.split(',')[1]
                          };
                          return newImages;
                        });
                      };
                      reader.readAsDataURL(file);
                    });
                  }}
                  className="hidden"
                  id="bulk-posture-upload"
                />
                <label htmlFor="bulk-posture-upload" className="cursor-pointer flex items-center justify-center gap-3 w-full p-4 border-2 border-dashed border-white/20 rounded-xl hover:border-amber-500/50 transition-colors">
                  <Upload className="text-slate-400" size={24} />
                  <div className="text-left">
                    <p className="text-slate-400 text-sm font-medium">Upload all 4 photos at once</p>
                    <p className="text-slate-500 text-xs">Select multiple images from your device</p>
                  </div>
                </label>
              </div>

              {/* Status & Clear */}
              <div className="flex items-center justify-between text-sm mb-4">
                <span className="text-slate-400">
                  <span className="text-amber-400 font-bold">{postureImages.filter(Boolean).length}</span> of 4 photos uploaded
                </span>
                {postureImages.some(Boolean) && (
                  <button 
                    onClick={() => {
                      setPostureImages([null, null, null, null]);
                      setPostureResult(null);
                    }}
                    className="text-red-400 hover:text-red-300 text-xs flex items-center gap-1"
                  >
                    <X size={12} /> Clear all photos
                  </button>
                )}
              </div>

              {/* Analyze Button */}
              <Button
                onClick={async () => {
                  const validImages = postureImages.filter(Boolean);
                  if (validImages.length === 0) {
                    toast.error("Please upload at least one photo");
                    return;
                  }
                  
                  setIsAnalyzingPosture(true);
                  setPostureResult(null);
                  
                  try {
                    const base64Images = validImages.map(img => `data:image/jpeg;base64,${img.base64}`);
                    
                    const response = await axios.post(`${API}/ai/analyze-posture`, {
                      images: base64Images,
                      analysis_type: "body_posture"
                    });
                    
                    if (response.data.success) {
                      setPostureResult(response.data.analysis);
                      toast.success("Body posture analysis complete!");
                    } else {
                      toast.error(response.data.error || "Analysis failed");
                    }
                  } catch (error) {
                    console.error("Posture analysis error:", error);
                    toast.error("Failed to analyze posture. Please try again.");
                  } finally {
                    setIsAnalyzingPosture(false);
                  }
                }}
                disabled={!postureImages.some(Boolean) || isAnalyzingPosture}
                className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 py-6 text-lg"
              >
                {isAnalyzingPosture ? (
                  <>
                    <Loader2 size={20} className="mr-2 animate-spin" />
                    Analyzing body posture...
                  </>
                ) : (
                  <>
                    <Sparkles size={20} className="mr-2" />
                    Analyze Body Posture ({postureImages.filter(Boolean).length} photo{postureImages.filter(Boolean).length !== 1 ? 's' : ''})
                  </>
                )}
              </Button>
            </div>

            {/* Posture Analysis Results */}
            {postureResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#111c2e] rounded-2xl p-6 border border-amber-500/30"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold flex items-center gap-2 text-amber-400">
                    <Check size={20} className="text-green-400" />
                    Body Posture Analysis Results
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-xs text-amber-400"
                    onClick={() => {
                      const text = Object.entries(postureResult)
                        .filter(([k, v]) => v && v !== 'normal')
                        .map(([k, v]) => `${k.replace(/_/g, ' ').toUpperCase()}: ${v}`)
                        .join('\n');
                      navigator.clipboard.writeText(text);
                      toast.success("Posture analysis copied!");
                    }}
                  >
                    <Copy size={12} className="mr-1" />
                    Copy All
                  </Button>
                </div>

                {/* Posture Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
                  {[
                    { key: 'neck', label: 'Neck / Head' },
                    { key: 'shoulder', label: 'Shoulder' },
                    { key: 'shoulder_blade', label: 'Shoulder Blade' },
                    { key: 'back_body', label: 'Back Body' },
                    { key: 'back_posture', label: 'Back / Humpback' },
                    { key: 'chest', label: 'Chest' },
                    { key: 'arm', label: 'Arm Position' },
                    { key: 'waist_height', label: 'Waist Height' },
                    { key: 'abdomen', label: 'Abdomen' },
                    { key: 'seat', label: 'Seat' },
                  ].map(({ key, label }) => {
                    const value = postureResult[key];
                    const isNormal = !value || value === 'normal';
                    return (
                      <div 
                        key={key}
                        className={`p-3 rounded-lg border ${
                          isNormal 
                            ? 'bg-[#0a1628] border-white/10' 
                            : 'bg-amber-500/10 border-amber-500/30'
                        }`}
                      >
                        <p className="text-xs text-slate-400 mb-1">{label}</p>
                        <p className={`text-sm font-medium ${isNormal ? 'text-slate-500' : 'text-amber-400'}`}>
                          {isNormal ? 'Normal' : value.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                      </div>
                    );
                  })}
                </div>

                {/* Notes */}
                {postureResult.notes && (
                  <div className="border-t border-white/10 pt-4 mt-4">
                    <h4 className="text-sm font-medium mb-2 text-slate-300 flex items-center gap-2">
                      <FileText size={14} />
                      AI Observations:
                    </h4>
                    <div className="bg-[#0a1628] rounded-xl p-4 text-sm text-slate-300 whitespace-pre-wrap">
                      {postureResult.notes}
                    </div>
                  </div>
                )}

                {/* Copy to POS Button */}
                <Button
                  variant="outline"
                  className="w-full mt-4 border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                  onClick={() => {
                    // Store posture result in localStorage for POS to pick up
                    localStorage.setItem('aiPostureResult', JSON.stringify(postureResult));
                    toast.success("Posture saved! Open POS to apply these results.");
                  }}
                >
                  <Sparkles size={16} className="mr-2" />
                  Save for POS Order
                </Button>
              </motion.div>
            )}

            {/* Tips */}
            <div className="mt-6 bg-[#111c2e] rounded-xl p-4 border border-white/10">
              <h4 className="font-medium mb-2 text-sm">📷 Tips for accurate posture analysis:</h4>
              <ul className="text-xs text-slate-400 space-y-1">
                <li>• Customer should stand naturally in a relaxed position</li>
                <li>• Take photos from front, left side, right side, and back</li>
                <li>• Customer should wear fitted clothing for accurate assessment</li>
                <li>• Ensure good lighting and a neutral background</li>
                <li>• Keep camera at chest height, perpendicular to the subject</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Fullscreen PDF Viewer Modal */}
      <AnimatePresence>
        {showPdfViewer && pdfViewerUrl && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex flex-col"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 bg-[#111c2e] border-b border-white/10">
              <div className="flex items-center gap-3">
                <FileText size={24} className="text-purple-400" />
                <div>
                  <h3 className="font-bold">Style Code Reference</h3>
                  <p className="text-xs text-slate-400">Scroll through to view all style images and codes</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={pdfViewerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors text-sm"
                >
                  <ExternalLink size={16} />
                  Download PDF
                </a>
                <button
                  onClick={() => setShowPdfViewer(false)}
                  className="w-10 h-10 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center hover:bg-red-500/30 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* PDF Viewer */}
            <div className="flex-1 p-4">
              <iframe
                src={pdfViewerUrl}
                className="w-full h-full rounded-lg bg-white"
                title="Style Code PDF Viewer"
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Image Viewer Modal */}
      <AnimatePresence>
        {showImageViewer && currentImagePages.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex flex-col"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 bg-[#111c2e] border-b border-white/10">
              <div className="flex items-center gap-3">
                <ImageIcon size={24} className="text-purple-400" />
                <div>
                  <h3 className="font-bold">{currentCategoryName}</h3>
                  <p className="text-xs text-slate-400">
                    Page {currentImagePages[currentImageIndex]} • Image {currentImageIndex + 1} of {currentImagePages.length}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-400">
                  {STYLE_CATEGORIES[selectedGarment].label} Style Guide
                </span>
                <button
                  onClick={() => setShowImageViewer(false)}
                  className="w-10 h-10 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center hover:bg-red-500/30 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Image Display */}
            <div className="flex-1 flex items-center justify-center p-4 relative">
              {/* Previous Button */}
              {currentImageIndex > 0 && (
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
                >
                  <ChevronLeft size={24} />
                </button>
              )}

              {/* Image or Loading State */}
              {isLoadingImage ? (
                <div className="flex flex-col items-center justify-center gap-4">
                  <Loader2 size={48} className="animate-spin text-purple-400" />
                  <p className="text-slate-400">Extracting page from PDF...</p>
                </div>
              ) : getCurrentImage() ? (
                <motion.img
                  key={currentImageIndex}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  src={getCurrentImage()}
                  alt={`${currentCategoryName} - Page ${currentImagePages[currentImageIndex]}`}
                  className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
                />
              ) : (
                <div className="flex flex-col items-center justify-center gap-4 text-slate-400">
                  <ImageIcon size={48} />
                  <p>Image not available</p>
                </div>
              )}

              {/* Next Button */}
              {currentImageIndex < currentImagePages.length - 1 && (
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
                >
                  <ChevronRight size={24} />
                </button>
              )}
            </div>

            {/* Thumbnail Strip */}
            {currentImagePages.length > 1 && (
              <div className="p-4 bg-[#111c2e] border-t border-white/10">
                <div className="flex justify-center gap-2 overflow-x-auto">
                  {currentImagePages.map((pageNum, idx) => {
                    const cacheKey = `${selectedGarment}_${pageNum}`;
                    const thumbImage = extractedImages[cacheKey];
                    return (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`h-16 w-24 rounded overflow-hidden border-2 transition-colors flex items-center justify-center ${
                          idx === currentImageIndex 
                            ? 'border-purple-500 bg-purple-500/20' 
                            : 'border-transparent hover:border-white/30 bg-white/5'
                        }`}
                      >
                        {thumbImage ? (
                          <img
                            src={thumbImage}
                            alt={`Page ${pageNum}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <span className="text-xs text-slate-400">P.{pageNum}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* ===== Style Cart (floating) ===== */}
      {/* Trigger button always visible bottom-right with item count badge */}
      <button
        onClick={() => setCartOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-purple-600 text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center"
        data-testid="rc-cart-fab"
        title="Style Cart"
      >
        <ShoppingCart size={20} />
        {cart.items.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[22px] h-5 flex items-center justify-center ring-2 ring-[#0a1628]">
            {cart.items.length}
          </span>
        )}
      </button>

      <AnimatePresence>
        {cartOpen && (
          <motion.div
            initial={{ opacity: 0, x: 360 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 360 }}
            className="fixed bottom-24 right-6 z-40 w-[360px] max-h-[70vh] bg-[#0a1628] border border-white/10 rounded-xl shadow-2xl flex flex-col overflow-hidden"
            data-testid="rc-cart-panel"
          >
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-amber-500/10 to-purple-500/10">
              <div className="flex items-center gap-2">
                <ShoppingCart size={14} className="text-amber-400" />
                <span className="text-sm font-medium">Style Cart</span>
                <span className="text-[10px] text-slate-400">({cart.items.length})</span>
              </div>
              <div className="flex items-center gap-1">
                {cart.items.length > 0 && (
                  <button
                    onClick={() => { clearStyleCart(); toast.success("Cart cleared"); }}
                    className="text-[10px] text-slate-400 hover:text-red-400 px-1.5"
                    data-testid="rc-cart-clear"
                  >
                    Clear
                  </button>
                )}
                <button onClick={() => setCartOpen(false)} className="text-slate-400 hover:text-white">
                  <X size={14} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
              {cart.items.length === 0 ? (
                <p className="text-xs text-slate-500 text-center py-6 px-3">
                  Tap <span className="text-amber-400">+ Add</span> on any code to stage it here, then push the whole stack to POS or Quick Order in one tap.
                </p>
              ) : (
                cart.items.map((it) => (
                  <div
                    key={it.code}
                    className="flex items-center gap-2 bg-white/5 rounded p-2 text-xs hover:bg-white/10 transition-colors"
                    data-testid={`rc-cart-item-${it.code}`}
                  >
                    <span className="flex-1 truncate">
                      <span className="text-slate-200">{it.label || it.value}</span>
                      <span className="text-slate-500 ml-1">→ {it.field}</span>
                    </span>
                    <code className="text-[10px] text-amber-400 font-mono shrink-0">{it.code}</code>
                    <button
                      onClick={() => removeFromStyleCart(it.code)}
                      className="text-slate-500 hover:text-red-400 shrink-0"
                      data-testid={`rc-cart-remove-${it.code}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="p-2 border-t border-white/10 grid grid-cols-2 gap-2">
              <Button
                size="sm"
                disabled={!cart.items.length}
                onClick={() => sendCartTo("/admin/pos")}
                className="bg-amber-500 hover:bg-amber-600 text-black text-xs"
                data-testid="rc-cart-send-pos"
              >
                Send → POS
              </Button>
              <Button
                size="sm"
                disabled={!cart.items.length}
                onClick={() => sendCartTo("/admin/quick-order")}
                className="bg-purple-600 hover:bg-purple-700 text-white text-xs"
                data-testid="rc-cart-send-quick"
              >
                Send → Quick Order
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminRCCodeBookPage;
